/**
 * Geometry engine for the 3-element STOL high-lift airfoil.
 *
 * Faithful TypeScript port of the geometric primitives in
 * ~/himalaya/geometry/airfoils/build_estol_geometry.py. Each builder returns a
 * Selig-ordered closed polyline (the `flat` contour the Python writes via
 * write_selig), validated against the reference .dat files in
 * ./__fixtures__/ by geometry.test.ts to ~1e-6.
 *
 * Conventions match the reference: points are [x, y] tuples; main contour runs
 * upper-lip-tip → LE → lower-cut-pt → cove; NACA elements run upper TE corner →
 * LE → lower TE corner. Use toPoints() to convert to the {x, y} form the UI and
 * WASM glue expect.
 */

import type {
  AirfoilCoords,
  ConcentricCoveArc,
  CoveCutouts,
  DeployTrack,
  FlaperonShape,
  HighLiftAirfoil,
  MotionLaw,
  V2,
  VaneShape,
} from './estolConfig';
import { PURE_ROTATION_MOTION } from './estolConfig';
import { elementsOf, camberMidpointY } from './elements';
import type { ElementKind, SlatElement, KruegerElement, MainCoveElement } from './elements';
import { evaluateBSpline } from './bspline';

// ---------------------------------------------------------------------------
// Vector / array helpers (numpy-equivalent semantics)
// ---------------------------------------------------------------------------

const sub = (a: V2, b: V2): V2 => [a[0] - b[0], a[1] - b[1]];
const add = (a: V2, b: V2): V2 => [a[0] + b[0], a[1] + b[1]];
const scale = (a: V2, s: number): V2 => [a[0] * s, a[1] * s];
const dot = (a: V2, b: V2): number => a[0] * b[0] + a[1] * b[1];
const norm = (v: V2): number => Math.hypot(v[0], v[1]);
const unit = (v: V2): V2 => scale(v, 1 / norm(v));

/** Rotate points by `angleRad` (CCW) about `center`. Matches numpy `(p-c)@R.T+c`. */
function rotate(points: V2[], angleRad: number, center: V2 = [0, 0]): V2[] {
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  return points.map(([x, y]) => {
    const dx = x - center[0];
    const dy = y - center[1];
    return [dx * c - dy * s + center[0], dx * s + dy * c + center[1]] as V2;
  });
}

/** Inclusive linspace of `n` samples from `a` to `b` (numpy.linspace). */
function linspace(a: number, b: number, n: number): number[] {
  if (n === 1) return [a];
  const step = (b - a) / (n - 1);
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) out[i] = a + step * i;
  out[n - 1] = b; // exact endpoint, like numpy
  return out;
}

/**
 * Scalar linear interpolation matching numpy.interp: `xp` must be ascending;
 * values outside the range clamp to the endpoints.
 */
function interp(x: number, xp: number[], fp: number[]): number {
  const n = xp.length;
  if (x <= (xp[0] ?? 0)) return fp[0] ?? 0;
  if (x >= (xp[n - 1] ?? 0)) return fp[n - 1] ?? 0;
  let i = 1;
  while (i < n && (xp[i] ?? 0) < x) i++;
  const x0 = xp[i - 1] ?? 0;
  const x1 = xp[i] ?? 0;
  const t = (x - x0) / (x1 - x0);
  return (fp[i - 1] ?? 0) + ((fp[i] ?? 0) - (fp[i - 1] ?? 0)) * t;
}

/** Convert internal [x, y] tuples to the {x, y} form used by the UI / WASM glue. */
export function toPoints(contour: V2[]): { x: number; y: number }[] {
  return contour.map(([x, y]) => ({ x, y }));
}

/** Natural cubic spline through ascending samples `(xs, ys)`; returns an evaluator. */
function naturalCubicSpline(xs: number[], ys: number[]): (x: number) => number {
  const n = xs.length;
  const h = xs.slice(1).map((x, i) => x - (xs[i] ?? 0));
  const l = new Array<number>(n).fill(1);
  const mu = new Array<number>(n).fill(0);
  const z = new Array<number>(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const hi = h[i] ?? 0, hi1 = h[i - 1] ?? 0;
    const a = (3 / hi) * ((ys[i + 1] ?? 0) - (ys[i] ?? 0)) - (3 / hi1) * ((ys[i] ?? 0) - (ys[i - 1] ?? 0));
    l[i] = 2 * ((xs[i + 1] ?? 0) - (xs[i - 1] ?? 0)) - hi1 * (mu[i - 1] ?? 0);
    mu[i] = hi / (l[i] ?? 1);
    z[i] = (a - hi1 * (z[i - 1] ?? 0)) / (l[i] ?? 1);
  }
  const c = new Array<number>(n).fill(0);
  const b = new Array<number>(n).fill(0);
  const d = new Array<number>(n).fill(0);
  for (let j = n - 2; j >= 0; j--) {
    const hj = h[j] ?? 0;
    c[j] = (z[j] ?? 0) - (mu[j] ?? 0) * (c[j + 1] ?? 0);
    b[j] = ((ys[j + 1] ?? 0) - (ys[j] ?? 0)) / hj - (hj * ((c[j + 1] ?? 0) + 2 * (c[j] ?? 0))) / 3;
    d[j] = ((c[j + 1] ?? 0) - (c[j] ?? 0)) / (3 * hj);
  }
  return (x) => {
    let i = n - 2;
    while (i > 0 && x < (xs[i] ?? 0)) i--;
    const t = x - (xs[i] ?? 0);
    return (ys[i] ?? 0) + t * ((b[i] ?? 0) + t * ((c[i] ?? 0) + t * (d[i] ?? 0)));
  };
}

/**
 * Spline-smooth a tabulated airfoil onto a dense, cosine-clustered surface. The raw
 * tables are coarse (LS(1)-0417 is 25 points/surface, ~0.05c apart mid-chord); drawn or
 * meshed as straight segments they facet the surface, which inflates RANS drag.
 *
 * A SINGLE parametric spline x(s), y(s) runs around the whole section (upper-TE → LE →
 * lower-TE) parameterized by arc length, so the leading edge is an interior knot and the
 * nose stays C² smooth. (Two separate per-surface splines leave each side with its own
 * natural end-condition at the LE → a corner.) Each surface is then resampled from that
 * one spline, cosine-clustered dense at the LE and TE. Returns LE→TE arrays as before.
 */
export function densifyAirfoil(c: AirfoilCoords, n = 160): AirfoilCoords {
  const loop: V2[] = [...c.upper.slice().reverse(), ...c.lower.slice(1)];   // upper-TE → LE → lower-TE
  const s = [0];
  for (let i = 1; i < loop.length; i++) {
    const cur = loop[i], prev = loop[i - 1];
    if (!cur || !prev) continue;
    s.push((s[i - 1] ?? 0) + norm(sub(cur, prev)));
  }
  const fx = naturalCubicSpline(s, loop.map((p) => p[0]));
  const fy = naturalCubicSpline(s, loop.map((p) => p[1]));
  const sLE = s[c.upper.length - 1] ?? 0;                  // arc length at the LE (shared knot)
  const sEnd = s[s.length - 1] ?? 0;

  // sample [s0,s1] at n points, cosine-clustered at both ends (→ dense at LE and TE)
  const sample = (s0: number, s1: number): V2[] =>
    linspace(0, Math.PI, n).map((beta): V2 => {
      const sv = s0 + (s1 - s0) * 0.5 * (1 - Math.cos(beta));
      return [fx(sv), fy(sv)];
    });

  return { upper: sample(sLE, 0), lower: sample(sLE, sEnd) };  // both LE→TE
}

// ---------------------------------------------------------------------------
// Foil conditioning pre-step (FXC-9009 arbitrary-foil slat rework, §4)
//
// `conditionFoil` is the SINGLE ingest seam (wired at the densifyAirfoil chokepoint in
// buildConfiguration): it takes an arbitrary tabulated foil — the today's split {upper, lower}, a raw
// Selig single-list, or a Lednicer two-block, in any chord / rotation — and emits EXACTLY the
// AirfoilCoords doc-contract densifyAirfoil already requires (two ascending-x LE→TE surfaces sharing
// an LE node at [0,0], unit chord, TE near [1,0]). An already-canonical foil (the DEFAULT LS(1)-0417)
// is returned UNCHANGED so densify is byte-identical (D-Codebase-1). Detection is NOT folded into
// densify (§0 fact 3 / A8); densify still trusts the split this produces.
// ---------------------------------------------------------------------------

/**
 * Osculating LE radius of a surface from its near-LE points. The nose is parabolic in the LE-tangent
 * frame (x ≈ y²/(2r)); least-squares for the single parameter u = 1/(2r) over the off-LE points gives
 * u = Σ(x·y²)/Σ(y⁴), hence r = Σ(y⁴) / (2·Σ(x·y²)). This recovers the standard nose-radius (and the
 * upper/lower ASYMMETRY of a cambered foil) directly from the tabulated tip, where a generic circle
 * fit over a smoothed densified arc washes the asymmetry out. `pts` are LE→TE from the LE node.
 */
function fitNoseRadius(pts: V2[], xLo: number, xHi: number): number {
  const fit = (lo: number, hi: number): { r: number; n: number } => {
    let Sxyy = 0, Syyyy = 0, n = 0;
    for (const [x, y] of pts) {
      if (x < lo || x > hi) continue;        // skip the singular tip (x→0) and the flattening aft
      Sxyy += x * y * y;
      Syyyy += y * y * y * y;
      n++;
    }
    return { r: n >= 2 && Sxyy > 1e-18 ? Syyyy / (2 * Sxyy) : NaN, n };
  };
  let res = fit(xLo, xHi);
  // A COARSE-nosed table (no off-LE point inside the window) would otherwise collapse to NaN; widen to
  // the nearest off-LE points so a sparse foil degrades to a sensible estimate, not the band-minimum.
  if (res.n < 2) {
    const widened = pts.filter((p) => p[0] > 1e-9).slice(0, 6);
    res = fit(0, widened.length ? widened[widened.length - 1]![0] : xHi);
  }
  return res.r;
}

/**
 * The two nose radii (D-Aero-1, §4.2 step 7): the osculating LE radius of EACH surface over the first
 * `nNose` near-LE points. A slat is a suction-surface device — size the slot/cove off the UPPER nose,
 * the cusp/feather off the LOWER — and on a cambered foil the two differ (LS(1)-0417 upper ≈ 2.5×
 * lower). Each radius is clamped to `[0.003, 0.08]c`; values outside that band are digitisation
 * artefacts (the clamp is the falsifiable band OQ-3 calls for).
 */
export function noseRadii(coords: AirfoilCoords, xLo = 0.003, xHi = 0.025): { upper: number; lower: number } {
  const clampR = (r: number): number => (Number.isFinite(r) ? Math.max(0.003, Math.min(0.08, r)) : 0.003);
  return {
    upper: clampR(fitNoseRadius(coords.upper, xLo, xHi)),
    lower: clampR(fitNoseRadius(coords.lower, xLo, xHi)),
  };
}

/** A split foil already in the exact contract (ascending-x LE→TE halves, LE at [0,0], TE near [1,0]). */
function isCanonicalSplit(c: AirfoilCoords): boolean {
  const u = c.upper, l = c.lower;
  if (!u || !l || u.length < 8 || l.length < 8) return false;
  if (norm(u[0]!) > 1e-9 || norm(l[0]!) > 1e-9) return false;          // shared LE node at the origin
  for (let i = 1; i < u.length; i++) if ((u[i]![0]) < (u[i - 1]![0]) - 1e-12) return false;
  for (let i = 1; i < l.length; i++) if ((l[i]![0]) < (l[i - 1]![0]) - 1e-12) return false;
  if (Math.abs(u[u.length - 1]![0] - 1) > 1e-9 || Math.abs(l[l.length - 1]![0] - 1) > 1e-9) return false;
  return true;
}

/** Drop non-finite rows + exact-duplicate consecutive points. The wrap (first==last) is LEFT intact:
 *  for a sharp-TE Selig loop the two TE corners legitimately coincide and dropping one skews the TE. */
function sanitiseLoop(loop: V2[]): V2[] {
  const out: V2[] = [];
  for (const p of loop) {
    if (!p || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) continue;
    const prev = out[out.length - 1];
    if (prev && Math.hypot(p[0] - prev[0], p[1] - prev[1]) < 1e-12) continue;
    out.push([p[0], p[1]]);
  }
  return out;
}

/**
 * Order a RAW point list into a Selig loop (upper-TE → LE → lower-TE). Detects Selig (one list whose x
 * runs high→low→high) vs Lednicer (two LE→TE blocks each running low→high). An already-Selig list is
 * returned as-is; a Lednicer pair is stitched into Selig order.
 */
function orderRawLoop(raw: V2[]): V2[] {
  const pts = sanitiseLoop(raw);
  if (pts.length < 5) return pts;
  // Detect format with thresholds RELATIVE to the actual x-extent, so detection is invariant to chord
  // scale and x-offset (a Lednicer at chord 0.3 or offset off the origin must still be recognised, not
  // just the unit-chord LE-at-origin case). `range` is the chord extent; `xMin` the LE-side x.
  const xs = pts.map((p) => p[0]);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const range = Math.max(1e-9, xMax - xMin);
  // Lednicer: x starts near the LE, rises to the TE, RESETS back toward the LE, rises again. Find an
  // interior index where x drops by more than half the chord extent (the block boundary).
  let resetIdx = -1;
  for (let i = 1; i < pts.length; i++) {
    if ((pts[i]![0]) < (pts[i - 1]![0]) - 0.5 * range) { resetIdx = i; break; } // big backward x-jump
  }
  const startsAtLe = (pts[0]![0]) < xMin + 0.2 * range;
  if (resetIdx > 0 && startsAtLe) {
    // Two LE→TE blocks: [0, resetIdx) and [resetIdx, end). Stitch to Selig: reverse the first block
    // (TE→LE) then append the second (LE→TE).
    const blockA = pts.slice(0, resetIdx);           // LE→TE
    const blockB = pts.slice(resetIdx);              // LE→TE
    return [...blockA.slice().reverse(), ...blockB.slice(1)];
  }
  return pts; // already a Selig single-list (high→low→high)
}

/**
 * Normalise a loop to unit chord / LE-origin / TE-on-+x. TE = mean of the two extreme endpoints; LE =
 * the farthest loop point from the TE (camber-immune). Records nothing back (the display transform is
 * out of scope for v1, OQ-2) — every downstream parameter is interpreted in this conditioned frame.
 */
function normaliseLoop(loop: V2[]): V2[] {
  const first = loop[0]!, last = loop[loop.length - 1]!;
  const te: V2 = [0.5 * (first[0] + last[0]), 0.5 * (first[1] + last[1])];
  let leIdx = 0, best = -1;
  loop.forEach((p, i) => { const d = norm(sub(p, te)); if (d > best) { best = d; leIdx = i; } });
  const le = loop[leIdx]!;
  const chord = sub(te, le);
  const len = norm(chord) || 1;
  const ang = -Math.atan2(chord[1], chord[0]);          // de-rotate the chord onto +x
  const c = Math.cos(ang), s = Math.sin(ang);
  return loop.map((p): V2 => {
    const dx = p[0] - le[0], dy = p[1] - le[1];
    return [(dx * c - dy * s) / len, (dx * s + dy * c) / len];
  });
}

/** True-LE index in a NORMALISED loop: the point of max distance from the TE (≈[1,0]); robust for a
 *  sharp LE where curvature is singular (§4.2 step 4, Stage A wins). */
function detectLeIndex(loop: V2[]): number {
  const te: V2 = [1, 0];
  let leIdx = 0, best = -1;
  loop.forEach((p, i) => { const d = norm(sub(p, te)); if (d > best) { best = d; leIdx = i; } });
  return leIdx;
}

/** Split a normalised Selig loop at the LE into ascending-x LE→TE halves sharing the LE node. The
 *  branch with greater y just off the LE is the UPPER surface (§4.2 step 5). */
function splitAtLe(loop: V2[], leIdx: number): AirfoilCoords {
  const branchA = loop.slice(0, leIdx + 1).slice().reverse(); // LE→(loop start TE)
  const branchB = loop.slice(leIdx);                          // LE→(loop end TE)
  const yOff = (b: V2[]): number => (b[1]?.[1] ?? b[0]?.[1] ?? 0);
  const aUp = yOff(branchA) >= yOff(branchB);
  return aUp ? { upper: branchA, lower: branchB } : { upper: branchB, lower: branchA };
}

/** Snap both surfaces to a shared TE station (close an open/blunt TE); keeps the recomposed loop a
 *  single defined-blunt-TE section the mesher accepts (§4.2 step 9). */
function closeTe(coords: AirfoilCoords): AirfoilCoords {
  const u = coords.upper, l = coords.lower;
  const uTe = u[u.length - 1]!, lTe = l[l.length - 1]!;
  const xTe = Math.min(uTe[0], lTe[0]);
  const clip = (s: V2[]): V2[] => {
    const sx = s.map((p) => p[0]), sy = s.map((p) => p[1]);
    const kept = s.filter((p) => p[0] < xTe - 1e-12);
    return [...kept, [xTe, interp(xTe, sx, sy)]];
  };
  // Only clip if the two ends disagree on the TE station (an open / mis-trimmed table); otherwise the
  // surfaces already share the TE and we leave them untouched.
  if (Math.abs(uTe[0] - lTe[0]) < 1e-9) return coords;
  return { upper: clip(u), lower: clip(l) };
}

/**
 * Condition an arbitrary tabulated foil into the exact AirfoilCoords contract densifyAirfoil requires
 * (§4). Accepts the today's split `{upper, lower}`, a raw Selig single-list, or a Lednicer two-block.
 * An already-canonical split foil (the DEFAULT) is returned UNCHANGED for byte-identity. Throws on a
 * foil with < 8 points/surface — a hard guard at the ingest boundary rather than rendering garbage.
 */
export function conditionFoil(input: AirfoilCoords | V2[]): AirfoilCoords {
  if (!Array.isArray(input) && isCanonicalSplit(input)) return input; // DEFAULT path: identity

  const rawLoop = Array.isArray(input)
    ? orderRawLoop(input)
    : [...input.upper.slice().reverse(), ...input.lower.slice(1)]; // recompose exactly as densify does
  const loop = sanitiseLoop(rawLoop);
  if (loop.length < 5) throw new Error('conditionFoil: degenerate foil (too few points)');

  const normalised = normaliseLoop(loop);
  const leIdx = detectLeIndex(normalised);
  let coords = splitAtLe(normalised, leIdx);
  coords = closeTe(coords);

  // Hard post-conditions (§4.1): ≥8 pts/surface, LE at origin, unit chord. Fail loudly.
  if (coords.upper.length < 8 || coords.lower.length < 8) {
    throw new Error('conditionFoil: < 8 points per surface after conditioning');
  }
  if (norm(coords.upper[0]!) > 1e-6 || norm(coords.lower[0]!) > 1e-6) {
    throw new Error('conditionFoil: LE not at origin after conditioning');
  }
  // The TE sits near x=1; allow a few % slack for a blunt / open TE whose two surfaces end a touch
  // short of the mean-chord station (§4.1 "TE ≈ [1,0]").
  const teX = Math.max(coords.upper[coords.upper.length - 1]![0], coords.lower[coords.lower.length - 1]![0]);
  if (Math.abs(teX - 1) > 0.05) throw new Error('conditionFoil: chord not normalised to 1');
  return coords;
}

// ---------------------------------------------------------------------------
// NACA 4-digit airfoil (unit chord, LE at origin, TE near (1,0))
// ---------------------------------------------------------------------------

interface NacaSurfaces {
  xu: number[];
  yu: number[];
  xl: number[];
  yl: number[];
}

function nacaSurfaces(xs: number[], m: number, p: number, t: number): NacaSurfaces {
  const xu: number[] = [];
  const yu: number[] = [];
  const xl: number[] = [];
  const yl: number[] = [];
  for (const x of xs) {
    const yt =
      5 * t *
      (0.2969 * Math.sqrt(x) - 0.126 * x - 0.3516 * x * x + 0.2843 * x ** 3 - 0.1015 * x ** 4);
    let yc: number;
    let dyc: number;
    if (x < p) {
      yc = (m / (p * p)) * (2 * p * x - x * x);
      dyc = (2 * m / (p * p)) * (p - x);
    } else {
      const q = (1 - p) * (1 - p);
      yc = (m / q) * (1 - 2 * p + 2 * p * x - x * x);
      dyc = (2 * m / q) * (p - x);
    }
    const theta = Math.atan(dyc);
    const st = Math.sin(theta);
    const ct = Math.cos(theta);
    xu.push(x - yt * st);
    yu.push(yc + yt * ct);
    xl.push(x + yt * st);
    yl.push(yc - yt * ct);
  }
  return { xu, yu, xl, yl };
}

/**
 * Selig-ordered NACA 4-digit coordinates (unit chord) with an optional blunt TE
 * of vertical thickness `teThickness`. Port of `naca4()`.
 */
export function naca4(code: string, teThickness = 0.0, nPanels = 160): V2[] {
  const m = parseInt(code[0] ?? "0", 10) / 100.0;
  const p = parseInt(code[1] ?? "0", 10) / 10.0;
  const t = parseInt(code.slice(2, 4), 10) / 100.0;

  // Find the chordwise cut where the surface-to-surface gap equals teThickness.
  const xScan = linspace(0.5, 1.0, 5001);
  const s = nacaSurfaces(xScan, m, p, t);
  const gap = xScan.map((_, i) => Math.hypot((s.xu[i] ?? 0) - (s.xl[i] ?? 0), (s.yu[i] ?? 0) - (s.yl[i] ?? 0)));
  // numpy interp requires ascending xp: gap decreases toward TE, so reverse both.
  const gapRev = gap.slice().reverse();
  const xScanRev = xScan.slice().reverse();
  const xCut = interp(teThickness, gapRev, xScanRev);

  // Cosine-clustered chord stations scaled to the cut.
  const beta = linspace(0.0, Math.PI, Math.floor(nPanels / 2) + 1);
  const x = beta.map((b) => 0.5 * (1.0 - Math.cos(b)) * xCut);
  const surf = nacaSurfaces(x, m, p, t);

  const out: V2[] = [];
  // upper TE corner → LE (reversed)
  for (let i = x.length - 1; i >= 0; i--) out.push([surf.xu[i] ?? 0, surf.yu[i] ?? 0]);
  // LE → lower TE corner (skip duplicated LE point)
  for (let i = 1; i < x.length; i++) out.push([surf.xl[i] ?? 0, surf.yl[i] ?? 0]);
  return out;
}

// ---------------------------------------------------------------------------
// Anchor a unit-chord airfoil between explicit LE/TE coordinates
// ---------------------------------------------------------------------------

/** Port of `anchor_airfoil()`. */
export function anchorAirfoil(raw: V2[], targetLe: V2, targetTe: V2): V2[] {
  // raw LE = min-x point; raw TE = midpoint of first/last (the two TE corners).
  let leIdx = 0;
  for (let i = 1; i < raw.length; i++) if ((raw[i]?.[0] ?? Infinity) < (raw[leIdx]?.[0] ?? Infinity)) leIdx = i;
  const rawLe = raw[leIdx] ?? [0, 0];
  const rawFirst = raw[0] ?? [0, 0];
  const rawLast = raw[raw.length - 1] ?? [0, 0];
  const rawTe: V2 = [0.5 * (rawFirst[0] + rawLast[0]), 0.5 * (rawFirst[1] + rawLast[1])];
  const rawVec = sub(rawTe, rawLe);
  const tgtVec = sub(targetTe, targetLe);
  const sc = norm(tgtVec) / norm(rawVec);
  const angle = Math.atan2(tgtVec[1], tgtVec[0]) - Math.atan2(rawVec[1], rawVec[0]);
  const translated = raw.map((pt) => sub(pt, rawLe));
  const rotated = rotate(translated, angle);
  return rotated.map((pt) => add(scale(pt, sc), targetLe));
}

// ---------------------------------------------------------------------------
// Cove fillet
// ---------------------------------------------------------------------------

export interface FilletResult {
  /** Tangent point on the leg toward `pIn`. */
  tIn: V2;
  /** Arc samples from the `pIn` tangent to the `pOut` tangent. */
  arc: V2[];
  /** Tangent point on the leg toward `pOut`. */
  tOut: V2;
}

/** Port of `fillet_corner()`: round the corner at `vertex` with `radius`. */
export function filletCorner(pIn: V2, vertex: V2, pOut: V2, radius: number, nArc = 40): FilletResult {
  const v1 = unit(sub(pIn, vertex));
  const v2 = unit(sub(pOut, vertex));
  const alpha = Math.acos(Math.max(-1.0, Math.min(1.0, dot(v1, v2))));
  const half = alpha / 2.0;
  const tIn = add(vertex, scale(v1, radius / Math.tan(half)));
  const tOut = add(vertex, scale(v2, radius / Math.tan(half)));
  const center = add(vertex, scale(unit(add(v1, v2)), radius / Math.sin(half)));

  const aIn = Math.atan2(tIn[1] - center[1], tIn[0] - center[0]);
  const aOut = Math.atan2(tOut[1] - center[1], tOut[0] - center[0]);
  // Wrap the swept angle into (-pi, pi], matching the Python modulo expression.
  const delta = mod(aOut - aIn + Math.PI, 2 * Math.PI) - Math.PI;
  const arc = linspace(0.0, delta, nArc).map((d) => {
    const a = aIn + d;
    return [center[0] + radius * Math.cos(a), center[1] + radius * Math.sin(a)] as V2;
  });
  return { tIn, arc, tOut };
}

/** Python-style modulo (result has the sign of the divisor). */
function mod(a: number, b: number): number {
  return ((a % b) + b) % b;
}

/** Clip a polyline at the vertical line x = xCut: drop the leading points with x < xCut
 *  and start at the crossing point Q on x = xCut. Used for the spoiler's inner ceiling
 *  when it rotates in (the rotated underside pokes back over the hinge line). */
function clipAtX(poly: V2[], xCut: number): V2[] {
  for (let i = 0; i < poly.length - 1; i++) {
    const cur = poly[i], next = poly[i + 1];
    if (!cur || !next) continue;
    const x0 = cur[0], y0 = cur[1], x1 = next[0], y1 = next[1];
    if ((x0 < xCut) !== (x1 < xCut)) {                    // segment straddles x = xCut
      const t = (xCut - x0) / (x1 - x0);
      return [[xCut, y0 + t * (y1 - y0)], ...poly.slice(i + 1)];
    }
  }
  return poly;                                            // no crossing (θ ≈ 0)
}

/** Nearest intersection of the ray `origin + t·dir` (t ≥ 0) with a polyline, or null. */
function rayPolyline(origin: V2, dir: V2, poly: V2[]): V2 | null {
  let best: V2 | null = null;
  let bestT = Infinity;
  for (let i = 0; i < poly.length - 1; i++) {
    const a = poly[i], next = poly[i + 1];
    if (!a || !next) continue;
    const ex = next[0] - a[0];
    const ey = next[1] - a[1];
    const det = ex * dir[1] - ey * dir[0];                // cross(edge, dir); 0 ⇒ parallel
    if (Math.abs(det) < 1e-12) continue;
    const rx = a[0] - origin[0];
    const ry = a[1] - origin[1];
    const t = (ex * ry - ey * rx) / det;                  // ray parameter
    const s = (dir[0] * ry - dir[1] * rx) / det;          // segment parameter ∈ [0, 1]
    if (t >= -1e-12 && s >= -1e-9 && s <= 1 + 1e-9 && t < bestT) {
      bestT = t;
      best = [origin[0] + t * dir[0], origin[1] + t * dir[1]];
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Element builders
// ---------------------------------------------------------------------------

/**
 * Coved main wing. The cove follows the airfoil's own surfaces offset inward by
 * `bluntThickness`: the ceiling parallels the upper surface from `upperCutX`
 * forward to `coveX`; the floor parallels the lower surface from `coveX` out to
 * `lowerCutX`; a vertical back wall at `coveX` joins them with sharp corners. Each cut
 * carries a blunt-TE face struck at 30° to the x-axis from the outer skin point and ended
 * where it meets the inner cove surface, so the outer side of each lip runs slightly longer
 * than the inside of the cove (the exact length follows the local skin slope). Returns the `flat`
 * contour: upper-lip-tip → LE → lower-surface → lower-cut → (blunt) → floor → back
 * wall → ceiling → lip-bottom, closing the upper blunt TE back to the lip tip.
 *
 * ``spoilerDeg`` deflects the upper cove lip (coveX→upperCutX, outer + ceiling) as a
 * rigid flap hinged on the upper surface at ``coveX`` — θ>0 lifts the tip. The outer
 * surface stays gap-free (a hinge kink); the inner ceiling is bridged (θ≥0) or clipped
 * at the hinge line (θ<0) so the cove stays a single closed curve.
 *
 * ``coveSide`` selects which surface the cove opens on: +1 (default) = TE-DOWN cove on the
 * lower/aft surface with the upper lip (today's behaviour); -1 = AILERON-UP cove, the exact
 * geometric mirror on the UPPER surface with the lower lip. The mirror is realised by
 * reflecting the section about the chord (y → -y) on entry, running the proven upper-cove
 * construction verbatim, then reflecting the resulting contour back — so the lower cove is a
 * pixel-exact mirror of the upper cove with zero new arithmetic, and the function is provably
 * identity when ``coveSide === 1``. The spoiler θ<0 clipAtX fold-back keys off the θ sign (not
 * the surface) so it transfers under the reflection unchanged; the reflection also negates the
 * effective spoiler rotation, so ``spoilerDeg`` is pre-negated for ``coveSide === -1`` to keep
 * "tip up" meaning tip up in world space.
 */
export function buildMainWing(airfoil: AirfoilCoords, cutouts: CoveCutouts,
                              bluntThickness: number, spoilerDeg = 0,
                              coveSide: 1 | -1 = 1): V2[] {
  if (coveSide === -1) {
    // Reflect the section about the chord, build the proven upper-cove, reflect back. The
    // y-flip negates the spoiler rotation sense, so pre-negate spoilerDeg to preserve "tip up".
    const mirrored: AirfoilCoords = {
      upper: airfoil.lower.map((q): V2 => [q[0], -q[1]]),
      lower: airfoil.upper.map((q): V2 => [q[0], -q[1]]),
    };
    const built = buildMainWing(mirrored, cutouts, bluntThickness, -spoilerDeg, 1);
    return built.map((p): V2 => [p[0], -p[1]]);
  }
  // HINGE-CONCENTRIC cove (opt-in — the internally-hinged sealed aileron, aircraft-CS seed): carve a
  // DEEP ROUNDED pocket whose back wall is a circular arc of `arc.radius` about `arc.hinge`, spanning
  // the whole angular range the flap's concentric nose (radius arc.radius − gap) sweeps. Because the
  // wall is a circle centred on the rotation hinge, the flap nose clears it by a CONSTANT gap at every
  // deflection. Gated on `cutouts.arc` + the flat (spoiler-free) TE-down regime the seed uses; absent
  // ⇒ the legacy vertical-wall cove below is byte-identical. (The seed never sets a spoiler; a spoiler
  // + concentric combination is out of scope, so it falls through to the legacy path.)
  if (cutouts.arc && spoilerDeg === 0) {
    return buildConcentricMainCove(airfoil, cutouts, cutouts.arc);
  }

  const upper = airfoil.upper;
  const lower = airfoil.lower;
  const { upperCutX: xUpCut, lowerCutX: xLoCut, coveX } = cutouts;

  const upX = upper.map((q) => q[0]);
  const upY = upper.map((q) => q[1]);
  const loX = lower.map((q) => q[0]);
  const loY = lower.map((q) => q[1]);
  const yUpAtCut = interp(xUpCut, upX, upY);
  const yLoAtCut = interp(xLoCut, loX, loY);
  const yUpCove = interp(coveX, upX, upY);
  const yLoCove = interp(coveX, loX, loY);

  // Each cut face is a straight line at 30° to the x-axis, struck from the outer skin point and
  // terminated where it meets the inner cove surface (the skin offset inward by bt). The skin is
  // sloped, so the inner endpoint's x (and z) shift — we intersect rather than assume a fixed
  // length — and the outer skin ends up running slightly longer than the inside of the cove.
  const c30 = Math.cos(Math.PI / 6);
  const s30 = Math.sin(Math.PI / 6);
  const floorSurf: V2[] = lower.map((q) => [q[0], q[1] + bluntThickness]); // cove floor surface
  const ceilSurf: V2[] = upper.map((q) => [q[0], q[1] - bluntThickness]);  // cove ceiling surface

  const loOuter: V2 = [xLoCut, yLoAtCut];
  // ray up-and-forward into the cove; terminate on the floor surface
  const lowerCutTop: V2 = rayPolyline(loOuter, [-c30, s30], floorSurf) ?? [xLoCut, yLoAtCut + bluntThickness];

  const loKept: V2[] = [...lower.filter((q) => q[0] < xLoCut), loOuter];

  // Cove floor: lower surface offset +bt, from the cut-face landing point forward to coveX.
  const floorMid: V2[] = lower
    .filter((q) => q[0] > coveX && q[0] < lowerCutTop[0])
    .sort((a, b) => b[0] - a[0])                                 // decreasing x
    .map((q) => [q[0], q[1] + bluntThickness]);
  const floorCove: V2 = [coveX, yLoCove + bluntThickness];       // back-wall bottom corner

  // Spoiler: rotate the upper lip (coveX→upperCut, outer + inner ceiling) rigidly about
  // the hinge H on the upper surface at coveX (θ>0 = tip up).
  const H: V2 = [coveX, yUpCove];
  const theta = (spoilerDeg * Math.PI) / 180;
  const upOuter: V2 = [xUpCut, yUpAtCut];
  // ray down-and-forward into the cove; terminate on the ceiling surface
  const upInner: V2 = rayPolyline(upOuter, [-c30, -s30], ceilSurf) ?? [xUpCut, yUpAtCut - bluntThickness];
  const lipUpper = upper.filter((q) => q[0] > coveX && q[0] < xUpCut);
  const lipOuter = rotate([H, ...lipUpper, upOuter], theta, H);                     // O(x); [0]=H; outer skin runs to xUpCut
  const P0: V2 = [coveX, yUpCove - bluntThickness];                                 // undeflected ceiling root
  const lipInnerMid = lipUpper.filter((q) => q[0] < upInner[0]);                    // ceiling stops at the cut-face landing
  const lipInner = rotate(
    [P0, ...lipInnerMid.map((q): V2 => [q[0], q[1] - bluntThickness]), upInner],
    theta, H);                                                                       // L(x); [0]=L(coveX)

  // Inner ceiling root: θ≥0 opens a gap → bridge the back wall (P0) to the lifted
  // underside L(coveX); θ<0 → the underside crosses back over coveX → clip it there.
  const lipInnerRoot = lipInner[0] ?? P0;
  const ceiling: V2[] = theta >= 0
    ? (norm(sub(P0, lipInnerRoot)) > 1e-9 ? [P0, ...lipInner] : lipInner)
    : clipAtX(lipInner, coveX);

  const upFixed = upper.filter((q) => q[0] < coveX);             // LE → coveX (fixed upper)
  return [
    ...lipOuter.slice().reverse(),   // rotated lip outer: tip → H
    ...upFixed.slice().reverse(),    // fixed upper: coveX → LE
    ...loKept.slice(1),              // LE → lower cut point
    lowerCutTop,                     // lower blunt-TE edge
    ...floorMid,                     // floor: lowerCut → coveX
    floorCove,                       // back-wall bottom
    ...ceiling,                      // back-wall top / treatment → ceiling → rotated inner tip
  ];                                 // closes rotated inner tip → lip tip
}

/**
 * Deep HINGE-CONCENTRIC main cove (FXC-9009 internally-hinged sealed aileron; ported from the CS
 * designer's `compute_cs_shape` spiral cove). Carves a rounded pocket into the main whose BACK WALL
 * is the SPIRAL (`arc.rLo + gap` → `arc.rUp + gap`, cosine-blended) about `arc.hinge`. The pocket
 * opens between the two LIP points where that spiral crosses the airfoil's upper + lower surfaces;
 * forward of the lips the surfaces are the airfoil, aft of them is the open recess. The flap's
 * concentric nose spiral (the SAME profile minus `gap`) clears this wall by a CONSTANT gap at every
 * deflection.
 *
 * Selig-ordered like {@link buildMainWing}: upper lip → LE (upper skin), LE → lower lip (lower skin),
 * then the spiral back wall lower lip → (−x front) → upper lip. Pure geometry; no spoiler / coveSide
 * mirror (the seed uses neither — {@link buildMainWing} only calls this when `spoilerDeg === 0`).
 */
function buildConcentricMainCove(
  airfoil: AirfoilCoords,
  cutouts: CoveCutouts,
  arc: ConcentricCoveArc,
  nSamples = 80,
): V2[] {
  void cutouts;
  const upper = airfoil.upper;
  const lower = airfoil.lower;
  const { hinge, rLo, rUp, aLo, aUp, gap } = arc;

  // Cove spiral = the flap-nose spiral profile offset OUT by `gap` (so the flap nose clears it by gap).
  const cove = spiralArc(hinge, rLo + gap, rUp + gap, aLo, aUp, nSamples); // ordered lower → front → upper

  // Where the cove spiral crosses each surface = the wing lip (the aftmost intact-skin point). Walk the
  // spiral and find the first segment straddling the surface (arc-z vs interpolated surface-z at arc-x).
  const surfHit = (surf: V2[]): { i: number; x: number; z: number } | null => {
    const sx = surf.map((q) => q[0]);
    const sz = surf.map((q) => q[1]);
    const xMin = sx[0] ?? 0;
    const xMax = sx[sx.length - 1] ?? 1;
    for (let i = 0; i < cove.length - 1; i++) {
      const a = cove[i]!;
      const b = cove[i + 1]!;
      if (a[0] < xMin || a[0] > xMax || b[0] < xMin || b[0] > xMax) continue;
      const d0 = a[1] - interp(a[0], sx, sz);
      const d1 = b[1] - interp(b[0], sx, sz);
      if (d0 * d1 < 0) {
        const t = d0 / (d0 - d1);
        return { i, x: a[0] + t * (b[0] - a[0]), z: a[1] + t * (b[1] - a[1]) };
      }
    }
    return null;
  };
  const hitUp = surfHit(upper);
  const hitLo = surfHit(lower);

  // Trim the spiral to the stretch between the two surface hits (the open pocket wall), and trim the
  // wing skins forward of the lips. Fall back to the authored split x's if a hit is missing.
  const xUpLip = hitUp ? hitUp.x : cutouts.upperCutX;
  const xLoLip = hitLo ? hitLo.x : cutouts.lowerCutX;
  const upLip: V2 = hitUp ? [hitUp.x, hitUp.z] : [xUpLip, interp(xUpLip, upper.map((q) => q[0]), upper.map((q) => q[1]))];
  const loLip: V2 = hitLo ? [hitLo.x, hitLo.z] : [xLoLip, interp(xLoLip, lower.map((q) => q[0]), lower.map((q) => q[1]))];
  const loI = hitLo ? hitLo.i : 0;
  const hiI = hitUp ? hitUp.i : cove.length - 1;
  // Start the wall at the first spiral sample INSIDE the pocket. `cove[loI]` is the straddling
  // segment's OUTSIDE end: keeping it right after the exact `loLip` doubles the contour back on
  // itself by ~1e-3 c - invisible on canvas, but a self-intersecting boundary the RANS mesher's
  // Delaunay recovery rejects ("missing edge" / CDT failed status 5), so every seeded
  // concentric-aileron solve died before mesh2d.vtk landed. The upper end already closes
  // lip-exact (cove[hiI] is the inside end and the loop closes onto upFwd[0] = upLip), so only
  // the lower end needs the clip; the fallback (no surface hit) keeps the full spiral as before.
  const wallFirst = Math.min(loI, hiI) + (hitLo && hitUp ? 1 : 0);
  const wall = cove.slice(wallFirst, Math.max(loI, hiI) + 1); // lower lip → front → upper lip

  // Surfaces forward of the lips (the intact main), Selig-ordered like buildMainWing.
  const upFwd: V2[] = [upLip, ...upper.filter((q) => q[0] < xUpLip).slice().reverse()]; // upLip → … → LE
  const loFwd: V2[] = [...lower.filter((q) => q[0] < xLoLip), loLip];                   // LE → … → loLip
  return [
    ...upFwd,             // upper lip → LE (upper skin, forward of the lip)
    ...loFwd.slice(1),    // LE → lower lip (lower skin; drop the shared LE)
    ...wall,              // lower lip → (−x front spiral) → upper lip: the concentric back wall
  ];
}

/** Vane: anchored NACA airfoil. Port of `build_naca_element()` (`flat`). */
export function buildNacaElement(
  code: string,
  teThickness: number,
  targetLe: V2,
  targetTe: V2,
  nPanels = 160,
): V2[] {
  const raw = naca4(code, teThickness, nPanels);
  return anchorAirfoil(raw, targetLe, targetTe);
}

/** Forward (decreasing-x) unit tangent of a tabulated surface at chordwise `xc`. */
function forwardTangent(xs: number[], ys: number[], xc: number): V2 {
  const h = 1e-3;
  const x0 = Math.max(xs[0] ?? 0, xc - h);
  const x1 = Math.min(xs[xs.length - 1] ?? 0, xc + h);
  const dx = x1 - x0;
  const dy = interp(x1, xs, ys) - interp(x0, xs, ys);
  return unit([-dx, -dy]);
}

/**
 * Flaperon contour, derived from the main airfoil (not an independent foil). Aft
 * of the cuts the upper/lower surfaces are exactly the airfoil, blunt-truncated at
 * the TE by `bluntThickness` (matching the other elements). Forward of the cuts the
 * nose is a clamped cubic B-spline through 5 control points: the two cut points,
 * two tangent-handle points along the airfoil slope at each cut (lengths
 * `noseTangent{Upper,Lower}`), and the free middle `noseTip` — so the nose meets the
 * airfoil with matching slope (C1) at each cut. Built in the airfoil (global) frame,
 * Selig-ordered: upper-TE-corner → upper-cut → nose → lower-cut → lower-TE-corner.
 */
/**
 * The five B-spline nose control points (stowed/airfoil frame): the two cut points
 * (P0, P4), the two slope-locked tangent handles (P1, P3), and the free middle
 * `noseTip` (P2). Exposed so the UI can draw them.
 */
export function flaperonControlPoints(airfoil: AirfoilCoords, shape: FlaperonShape): [V2, V2, V2, V2, V2] {
  const { upperCutX, lowerCutX, noseTangentUpper, noseTangentLower, noseTip } = shape;
  const upX = airfoil.upper.map((q) => q[0]);
  const upY = airfoil.upper.map((q) => q[1]);
  const loX = airfoil.lower.map((q) => q[0]);
  const loY = airfoil.lower.map((q) => q[1]);
  const P0: V2 = [upperCutX, interp(upperCutX, upX, upY)];
  const P4: V2 = [lowerCutX, interp(lowerCutX, loX, loY)];
  const P1: V2 = add(P0, scale(forwardTangent(upX, upY, upperCutX), noseTangentUpper));
  const P3: V2 = add(P4, scale(forwardTangent(loX, loY, lowerCutX), noseTangentLower));
  return [P0, P1, noseTip, P3, P4];
}

export function buildFlaperon(airfoil: AirfoilCoords, shape: FlaperonShape, bluntThickness: number): V2[] {
  const { upperCutX, lowerCutX } = shape;
  const upX = airfoil.upper.map((q) => q[0]);
  const upY = airfoil.upper.map((q) => q[1]);
  const loX = airfoil.lower.map((q) => q[0]);
  const loY = airfoil.lower.map((q) => q[1]);

  // Blunt-truncate the TE: gap = yUp - yLo decreases to 0 at the TE; find the x
  // (nearest the TE) where it equals bluntThickness. Reverse for ascending interp.
  const scan = linspace(0.6, 1.0, 2001);
  const gap = scan.map((x) => interp(x, upX, upY) - interp(x, loX, loY));
  const xTE = interp(bluntThickness, gap.slice().reverse(), scan.slice().reverse());

  const ctrl = flaperonControlPoints(airfoil, shape);
  const [P0, , , , P4] = ctrl;
  const cps = ctrl.map((p, i) => ({ x: p[0], y: p[1], id: String(i) }));
  const nose: V2[] = evaluateBSpline(cps, 3, 41).map((p) => [p.x, p.y]);

  // Airfoil surfaces aft of each cut, blunt-truncated at xTE.
  const upperAft: V2[] = [P0, ...airfoil.upper.filter((q) => q[0] > upperCutX && q[0] < xTE), [xTE, interp(xTE, upX, upY)]];
  const lowerAft: V2[] = [P4, ...airfoil.lower.filter((q) => q[0] > lowerCutX && q[0] < xTE), [xTE, interp(xTE, loX, loY)]];

  return [
    ...upperAft.slice().reverse(),   // upper TE corner → upper cut (P0)
    ...nose.slice(1, -1),            // nose interior (P0..P4 endpoints dropped — they're the cuts)
    ...lowerAft,                     // lower cut (P4) → lower TE corner
  ];
}

/**
 * Plain-flap (aileron) aft contour, built from the main airfoil exactly like {@link buildFlaperon}:
 * aft of `shape.upperCutX`/`lowerCutX` the surfaces are the airfoil (blunt-truncated at the TE),
 * forward of them the nose is a slope-matched clamped cubic B-spline that mates the cove the main
 * leaves at the hinge. Returned in the STOWED airfoil frame; the caller rotates it about the hinge
 * by the deflection. (A plain flap is the cove-and-gap mechanism the spoiler already demonstrates,
 * mirrored to the trailing edge — see {@link buildMainWing}'s cove + 30° blunt-cut faces.)
 */
export function buildPlainFlap(airfoil: AirfoilCoords, shape: FlaperonShape, bluntThickness: number): V2[] {
  // The aft piece shares the flaperon's blunt-TE scan + aft-surfaces + cove-mating nose, so it
  // reuses the same builder verbatim (the nose mates the cove the main leaves at the hinge).
  return buildFlaperon(airfoil, shape, bluntThickness);
}

/**
 * HINGE-CONCENTRIC plain-flap (aileron) aft contour — the internally-hinged sealed aileron nose
 * (FXC-9009 control-surface tranche, aircraft-CS seed; ported from the CS designer's spiral-nose
 * `compute_cs_shape`). AFT of the split x's (`shape.upperCutX` = upper split, `shape.lowerCutX` =
 * lower split) the surfaces are EXACTLY the airfoil (same blunt-TE scan + aft surfaces as
 * {@link buildFlaperon}). FORWARD of the splits the nose is a SPIRAL arc about the `arc.hinge`: its
 * radius blends (cosine) from the lower split radius `arc.rLo` at the lower split to the upper split
 * radius `arc.rUp` at the upper split, swept the long way through the −x front. The main cove uses the
 * SAME spiral + `arc.gap`, so the slot clearance stays exactly `gap` at every deflection. Returned in
 * the STOWED airfoil frame; the caller rotates it about the SAME hinge by the deflection.
 *
 * GATED alternative to {@link buildFlaperon}: only reached when the plain-flap element sets
 * `concentricNose` and carries a `cove.arc`, so non-concentric flaps (every existing caller) are
 * byte-identical.
 */
export function buildConcentricFlap(
  airfoil: AirfoilCoords,
  shape: FlaperonShape,
  bluntThickness: number,
  arc: ConcentricCoveArc,
  nSamples = 80,
): V2[] {
  const { upperCutX, lowerCutX } = shape;
  const { hinge, rLo, rUp, aLo, aUp } = arc;
  const upX = airfoil.upper.map((q) => q[0]);
  const upY = airfoil.upper.map((q) => q[1]);
  const loX = airfoil.lower.map((q) => q[0]);
  const loY = airfoil.lower.map((q) => q[1]);

  // Blunt-truncate the TE exactly like buildFlaperon (identical aft surfaces).
  const scan = linspace(0.6, 1.0, 2001);
  const gapScan = scan.map((x) => interp(x, upX, upY) - interp(x, loX, loY));
  const xTE = interp(bluntThickness, gapScan.slice().reverse(), scan.slice().reverse());

  // Split points (the aft-run endpoints; the spiral nose runs between them).
  const P0: V2 = [upperCutX, interp(upperCutX, upX, upY)];
  const P4: V2 = [lowerCutX, interp(lowerCutX, loX, loY)];

  // Spiral nose: sweep aLo → aUp the LONG way (through the −x front), radius cosine-blended rLo → rUp.
  const nose = spiralArc(hinge, rLo, rUp, aLo, aUp, nSamples);
  // Emit lower → upper along the spiral so it splices between the lower-aft and upper-aft runs; drop
  // the exact endpoints (P0/P4 are supplied by the aft runs) for watertight seams.
  const noseInterior = nose.slice(1, -1);

  // Airfoil surfaces aft of each split, blunt-truncated at xTE — IDENTICAL to buildFlaperon.
  const upperAft: V2[] = [P0, ...airfoil.upper.filter((q) => q[0] > upperCutX && q[0] < xTE), [xTE, interp(xTE, upX, upY)]];
  const lowerAft: V2[] = [P4, ...airfoil.lower.filter((q) => q[0] > lowerCutX && q[0] < xTE), [xTE, interp(xTE, loX, loY)]];

  return [
    ...upperAft.slice().reverse(),   // upper TE corner → upper split (P0)
    ...noseInterior.slice().reverse(),// upper split → (−x front spiral) → lower split
    ...lowerAft,                     // lower split (P4) → lower TE corner
  ];
}

/**
 * A cosine-blended SPIRAL arc about `hinge` (the concentric-aileron nose/cove primitive, ported from
 * `compute_cs_shape`). Sweeps the angle from `aLo` to `aUp` the LONG way (through the −x front, +2π so
 * the arc bulges forward), with the radius cosine-blended from `rLo` (at aLo) to `rUp` (at aUp).
 * Returns `nSamples` points ordered lower → front → upper.
 */
function spiralArc(hinge: V2, rLo: number, rUp: number, aLo: number, aUp: number, nSamples: number): V2[] {
  const twoPi = 2 * Math.PI;
  // Sweep from the lower split to the upper split the long way (matches compute_cs_shape: angles run
  // a_lo → a_lo − sweep, sweep = a_lo − a_up + 2π normalised into (0, 2π]).
  let sweep = aLo - aUp + twoPi;
  if (sweep > twoPi) sweep -= twoPi;
  const out: V2[] = [];
  for (let i = 0; i < nSamples; i++) {
    const t = nSamples > 1 ? i / (nSamples - 1) : 0;
    const a = aLo - t * sweep;
    const blend = (1 - Math.cos(Math.PI * t)) / 2;       // 0 at lower split → 1 at upper split
    const r = rLo + (rUp - rLo) * blend;
    out.push([hinge[0] + r * Math.cos(a), hinge[1] + r * Math.sin(a)]);
  }
  return out;
}

/**
 * Splice a FRISE HORN into a flaperon/aileron aft contour (FXC-9009 control-surface tranche).
 *
 * A Frise aileron carries a protruding LOWER-surface nose beak/horn AHEAD of the hinge: when the
 * aileron deflects trailing-edge-UP (negative deflection), the horn swings DOWN below the wing's
 * lower surface, giving aerodynamic + drag balance (the classic Frise adverse-yaw mechanism). The
 * base contour ({@link buildFlaperon}) is Selig-ordered upper-TE → upper-cut (P0) → nose interior →
 * lower-cut (P4) → lower-TE. We grow the horn off the LOWER cut (P4, the forward-most lower nose
 * point): a small rounded beak that bulges FORWARD (toward the hinge, −x) and BELOW the lower
 * surface (−y), then rejoins P4 — so it stays watertight + continuous with the existing nose. The
 * arc size is `hornFraction · chord` (the beak's forward reach + downward drop).
 *
 * This is a PURE post-process of the base contour: with the horn off the caller never calls this, so
 * a non-Frise aileron is byte-identical. The horn lives in the STOWED airfoil frame; the caller's
 * hinge rotation carries it (so on TE-up deflection the forward-below beak rotates further down).
 */
export function friseHorn(P4: V2, noseTip: V2, hornFraction: number): V2[] {
  const reach = Math.max(0, hornFraction);
  if (reach <= 0) return [];
  // Forward direction along the lower nose: from the lower cut toward the nose tip (predominantly
  // −x), used to place the beak ahead of (forward of) P4. The beak also drops below the lower
  // surface (−y) so it protrudes under the wing on TE-up deflection.
  // The horn is a LOWER-surface beak: it bulges FORWARD (toward the hinge — the −x sense, set by where
  // the nose tip lies relative to the lower cut) and clearly BELOW the lower surface (−y). Forward and
  // drop are kept on INDEPENDENT axes so the downward drop is never cancelled by the nose's up-slope: a
  // naive "toward the nose tip" direction points UP here (the flaperon nose tip sits ABOVE the lower
  // cut), which left the stowed beak barely protruding (see review). On trailing-edge-UP (negative)
  // hinge rotation this forward-and-below beak then swings further down below the wing — the Frise
  // adverse-yaw mechanism — while the stowed beak is now unambiguously below the surface.
  const fx = noseTip[0] <= P4[0] ? -1 : 1; // forward along the chord (toward the nose / hinge)
  // Beak vertex at `along`·reach forward and `drop`·reach below the lower cut P4.
  const at = (along: number, drop: number): V2 => [P4[0] + fx * reach * along, P4[1] - reach * drop];
  // A small rounded beak: P4 (lower cut) → mid1 → mid2 → tip → back1 → back2 → back to P4, so it
  // inserts cleanly where the nose interior meets the lower-aft surface (which restarts at P4).
  const mid1 = at(0.45, 0.18);
  const mid2 = at(0.9, 0.5);
  const tip = at(1.0, 0.6);
  const back1 = at(0.55, 0.66);
  const back2 = at(0.18, 0.4);
  return [mid1, mid2, tip, back1, back2];
}

/**
 * A flaperon/aileron aft contour with an optional FRISE horn (FXC-9009 control-surface tranche).
 * With `frise` off this is EXACTLY {@link buildFlaperon} (byte-identical). With `frise` on it splices
 * a rounded lower-nose beak ahead of the hinge via {@link friseHorn}, keeping the loop watertight.
 */
export function buildFlaperonWithFrise(
  airfoil: AirfoilCoords,
  shape: FlaperonShape,
  bluntThickness: number,
  frise?: boolean,
  hornFraction = 0.03,
): V2[] {
  const base = buildFlaperon(airfoil, shape, bluntThickness);
  if (!frise) return base;
  const ctrl = flaperonControlPoints(airfoil, shape);
  const P4 = ctrl[4];
  const noseTip = ctrl[2];
  const horn = friseHorn(P4, noseTip, hornFraction);
  if (horn.length === 0) return base;
  // The lower cut P4 is the FIRST vertex of the lower-aft run, i.e. it appears in `base` at the seam
  // between the nose interior and the lower-aft surface. Find that occurrence and insert the horn
  // right BEFORE it: nose-interior → [horn …] → P4 → lower-aft. The horn beak protrudes below + ahead.
  const eq = (a: V2, b: V2) => Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9;
  let seam = base.findIndex((p) => eq(p, P4));
  // Fallback: the nose B-spline endpoints are dropped, so the lower-aft P4 is the unique exact match;
  // if (numerically) absent, attach at the contour midpoint seam to stay watertight.
  if (seam < 0) seam = base.length;
  return [...base.slice(0, seam), ...horn, ...base.slice(seam)];
}

/**
 * Leading-edge slat contour — the fore/aft MIRROR of {@link buildPlainFlap}. The slat is the part
 * of the main airfoil FORWARD of `cutX`: a rounded leading edge plus the upper+lower surfaces back
 * to the cut, blunt-closed at the cut by a vertical back face (the slat's own trailing edge, where
 * it mates the main's new leading-edge cove). Returned in the STOWED airfoil frame; the caller
 * rotates it about the hinge (LE-down/forward droop).
 *
 * Selig-ordered: upper-cut corner → upper surface → LE → lower surface → lower-cut corner, then the
 * vertical back face closes lower-cut → upper-cut.
 */
export function buildPlainSlat(airfoil: AirfoilCoords, cutX: number): V2[] {
  const upX = airfoil.upper.map((q) => q[0]);
  const upY = airfoil.upper.map((q) => q[1]);
  const loX = airfoil.lower.map((q) => q[0]);
  const loY = airfoil.lower.map((q) => q[1]);
  const yUpCut = interp(cutX, upX, upY);
  const yLoCut = interp(cutX, loX, loY);

  // Upper surface forward of the cut: cut corner → LE (decreasing x).
  const upperFwd: V2[] = [
    [cutX, yUpCut],
    ...airfoil.upper.filter((q) => q[0] < cutX).slice().reverse(),
  ];
  // Lower surface forward of the cut: LE → cut corner (increasing x; skip the duplicate LE point).
  const lowerFwd: V2[] = [
    ...airfoil.lower.filter((q) => q[0] < cutX).slice(1),
    [cutX, yLoCut],
  ];
  return [
    ...upperFwd, // upper cut → LE
    ...lowerFwd, // LE → lower cut
    // vertical back face closes lower cut → upper cut (the slat's blunt TE at the cut).
  ];
}

/**
 * Trim a closed main-element contour to remove the leading-edge region FORWARD of `cutX` (where a
 * slat has been split off), replacing it with a vertical back face at x = `cutX`. This is the
 * main's new leading-edge cove front. We keep every contour vertex with x ≥ `cutX` and insert the
 * crossing point on each segment that straddles the cut, so the result stays a single closed
 * polyline. A `cutX` at/forward of the existing LE is a no-op (nothing to trim).
 */
export function trimMainLeadingEdge(contour: V2[], cutX: number): V2[] {
  const n = contour.length;
  if (n < 3) return contour;
  let leX = Infinity;
  for (const p of contour) if (p[0] < leX) leX = p[0];
  if (cutX <= leX) return contour; // cut is at/forward of the LE — nothing to trim.

  const out: V2[] = [];
  for (let i = 0; i < n; i++) {
    const cur = contour[i]!;
    const next = contour[(i + 1) % n]!;
    const curIn = cur[0] >= cutX;
    const nextIn = next[0] >= cutX;
    if (curIn) out.push(cur);
    if (curIn !== nextIn) {
      // segment straddles the cut: insert the crossing point on x = cutX.
      const t = (cutX - cur[0]) / (next[0] - cur[0]);
      out.push([cutX, cur[1] + t * (next[1] - cur[1])]);
    }
  }
  return out;
}

/**
 * Carve a PRONOUNCED ROUNDED leading-edge slat-cove into the main where the slat nests, replacing the
 * blunt vertical clip of {@link trimMainLeadingEdge}. This is the fore/aft MIRROR of
 * {@link buildMainWing}'s trailing-edge cove: the same primitives (offset inner surfaces for the cove
 * floor/ceiling, 30° blunt cut faces struck from the outer skin via `rayPolyline`, a rounded front via
 * {@link filletCorner}), but tuned so the recess is a real scooped bowl rather than a sliver:
 *   • the floor/ceiling offsets are DECOUPLED from the thin `bluntThickness` lip — they grow to a
 *     `coveWall*` set by the LOCAL nose thickness, so the walls sit meaningfully inside the skin;
 *   • the offset TAPERS from the thin lip at the cut to the full depth at the back wall, keeping the
 *     throat near the cut an open slot (a deeply-rigged slat clears it) while the forward bowl is deep;
 *   • the back wall is a SHORT near-vertical segment whose two corners are filleted independently, so
 *     the front rounds into a smooth bowl instead of the degenerate near-vertical join the old single
 *     fillet collapsed to when floor & ceiling ran near-parallel.
 * The cove back wall sits at `cutX` and the pocket opens FORWARD (toward decreasing x) into the nose
 * the slat vacated; it does not push main material forward of `cutX`.
 *
 * Implemented as a pure x-reflection (x → -x) so the proven aft-opening cove arithmetic runs
 * verbatim and is reflected back — exactly the mirror trick the aileron-up cove uses about the chord.
 *
 * `contour` is the closed main polyline; `coords` the densified airfoil surfaces. Keeps every contour
 * vertex with x ≥ `cutX` (the aft body) and splices the rounded cove in for the trimmed nose. The
 * cove rounds the corner AT `cutX`; it does not push main material forward of `cutX`.
 */
export function buildLeadingEdgeCove(
  contour: V2[],
  coords: AirfoilCoords,
  cutX: number,
  bluntThickness: number,
  opts?: { coveDepth?: number; lowerBiased?: boolean },
): V2[] {
  // §5.7: a Krueger passes `lowerBiased=true` to scoop the LOWER nose deep and spare the upper LE.
  const lowerBiased = opts?.lowerBiased ?? false;
  const n = contour.length;
  if (n < 3) return contour;
  let leX = Infinity;
  for (const p of contour) if (p[0] < leX) leX = p[0];
  if (cutX <= leX) return contour; // cut is at/forward of the LE — nothing to carve.

  // The aft body: every contour vertex with x ≥ cutX, with the two straddle-crossing points on
  // x = cutX inserted, exactly like trimMainLeadingEdge. We then REPLACE the (single) vertical back
  // face those two crossings would bridge with the rounded cove pocket. Track the upper/lower
  // crossing y so the cove cut faces start from the true skin points at cutX.
  const kept: V2[] = [];
  let upperCrossY = interp(cutX, coords.upper.map((q) => q[0]), coords.upper.map((q) => q[1]));
  let lowerCrossY = interp(cutX, coords.lower.map((q) => q[0]), coords.lower.map((q) => q[1]));
  for (let i = 0; i < n; i++) {
    const cur = contour[i]!;
    const next = contour[(i + 1) % n]!;
    const curIn = cur[0] >= cutX;
    const nextIn = next[0] >= cutX;
    if (curIn) kept.push(cur);
    if (curIn !== nextIn) {
      const t = (cutX - cur[0]) / (next[0] - cur[0]);
      kept.push([cutX, cur[1] + t * (next[1] - cur[1])]);
    }
  }

  // Build the rounded cove pocket via x-reflection: reflect the section about x = 0 so the LE cove
  // (back wall at cutX, opening toward decreasing x) becomes an aft-opening cove (back wall at -cutX,
  // opening toward increasing x), run the SAME offset-surface + 30° rayPolyline primitives, round the
  // nose, then reflect every point back. The reflected upper stays the upper surface (y unchanged).
  const rUpper: V2[] = coords.upper.map((q): V2 => [-q[0], q[1]]).slice().reverse(); // ascending -x
  const rLower: V2[] = coords.lower.map((q): V2 => [-q[0], q[1]]).slice().reverse();
  const xCut = -cutX;
  const c30 = Math.cos(Math.PI / 6);
  const s30 = Math.sin(Math.PI / 6);

  // The local nose thickness at the cut (upper − lower skin gap). The cove must scoop a MEANINGFUL
  // fraction of this out — a wall offset tied only to the thin `bluntThickness` (≈0.003) hugs the
  // skin and yields a near-vertical sliver, not a rounded pocket. Decouple the two:
  //   • the thin cut-LIP stubs keep `bluntThickness` (the real blunt-TE lip on the slat lip);
  //   • the cove FLOOR/CEILING sit `coveWall*` inside the skin so the recess has real depth.
  // Asymmetric by design: the CEILING (upper, throat side) carries the pronounced concave scoop that
  // forms the converging slot the slat cusp tucks into, while the FLOOR (lower side) stays shallow so
  // a deeply-rigged slat (translated down+forward) clears the lower cove wall — keeping the slot
  // throat (not a spurious lower-wall near-contact) the governing gap.
  // A SLAT (lowerBiased=false) vacates the UPPER throat → ceiling-deep scoop. A KRUEGER
  // (lowerBiased=true) folds out of the LOWER skin and KEEPS the laminar upper LE → the recess must
  // bite the FLOOR deep and leave the CEILING shallow (the vertical mirror). Only these two depth
  // COEFFICIENTS swap: the lip stubs (bluntThickness, symmetric), the tapered walls, and the back-wall
  // fillet all read coveWallCeil/coveWallFloor BY NAME, so they follow the swap automatically. The
  // coefficient SUM (0.20+0.06) is invariant, so backGap + the fillet radius are unchanged and the
  // bowl simply mirrors to the lower nose.
  const localThickness = Math.max(1e-4, upperCrossY - lowerCrossY);
  const ceilCoef = lowerBiased ? 0.06 : 0.20;
  const floorCoef = lowerBiased ? 0.20 : 0.06;
  const coveWallCeil = Math.max(bluntThickness, ceilCoef * localThickness); // throat scoop (deep unless lowerBiased)
  const coveWallFloor = Math.max(bluntThickness, floorCoef * localThickness); // lower wall (deep when lowerBiased)

  // Outer skin points at the cut (reflected). y is the true skin y at cutX (carried from above).
  const loOuter: V2 = [xCut, lowerCrossY];
  const upOuter: V2 = [xCut, upperCrossY];
  // 30° blunt cut faces, struck from the outer skin INTO the cove (toward increasing -x = forward),
  // terminated on the inner cove surface — identical to buildMainWing's loOuter/upOuter rays. These
  // are the thin LIP faces: they land on the (shallow) `bluntThickness` offset so the lip stays a
  // crisp thin edge, while the cove floor/ceiling proper (below) sit deeper at `coveWall`.
  const lipFloor: V2[] = rLower.map((q): V2 => [q[0], q[1] + bluntThickness]);
  const lipCeil: V2[] = rUpper.map((q): V2 => [q[0], q[1] - bluntThickness]);
  const lowerCutTop: V2 = rayPolyline(loOuter, [-c30, s30], lipFloor) ?? [xCut, lowerCrossY + bluntThickness];
  const upInner: V2 = rayPolyline(upOuter, [-c30, -s30], lipCeil) ?? [xCut, upperCrossY - bluntThickness];

  // The cove is a bounded rounded RECESS, not the whole vacated nose: its back wall sits a modest
  // depth forward of the cut (analogous to the TE cove's coveX back wall sitting forward of the cut).
  // Deepen it (relative to the legacy 0.4·cutX) so the main's new LE is a PRONOUNCED rounded pocket
  // the slat cusp tucks into, never quite reaching back to the original LE. In reflected space the
  // back wall is at xBack = -(cutX - coveDepth) (larger -x = further forward).
  // Co-scaled depth (D-Codebase-2, §6.5): the proper-slat branch passes a `t_local`-aware depth so the
  // throat is a function of ONE length scale; Krueger / legacy plain-slat pass nothing and keep the
  // absolute `0.055` cap. On LS(1)-0417 the override resolves to the same 0.045 (`0.45·cutX` governs),
  // so the slat cove is byte-identical and the default (slat-less) main never reaches this branch.
  const coveDepth = Number.isFinite(opts?.coveDepth)
    ? Math.max(0, opts!.coveDepth!)
    : Math.min(0.055, 0.45 * Math.max(cutX, 0));
  const xBack = -(cutX - coveDepth);

  // Floor and ceiling running from each cut-face landing FORWARD to the cove back wall (increasing -x).
  // The offset is TAPERED: thin (≈bluntThickness, the lip) at the cut and growing to the full coveWall*
  // at the back wall. This keeps the THROAT near the cut a thin open lip (so a deeply-rigged slat,
  // translated down+forward, keeps the slot throat as the governing gap rather than colliding with a
  // ceiling pulled uniformly down into its path) while the FORWARD bowl is the pronounced rounded
  // scoop. `taper` ∈ [0,1] runs 0 at the lip landing → 1 at xBack.
  const ceilStartX = upInner[0];
  const floorStartX = lowerCutTop[0];
  const ceilSpan = Math.max(1e-6, xBack - ceilStartX);
  const floorSpan = Math.max(1e-6, xBack - floorStartX);
  const wallAt = (x: number, startX: number, span: number, full: number): number => {
    const taper = Math.max(0, Math.min(1, (x - startX) / span)); // 0 at lip → 1 at back wall
    // smoothstep so the lip blends gently into the deep bowl (no kink at the lip landing).
    const s = taper * taper * (3 - 2 * taper);
    return bluntThickness + (full - bluntThickness) * s;
  };
  const floorMid: V2[] = rLower
    .filter((q) => q[0] > floorStartX && q[0] < xBack)
    .sort((a, b) => a[0] - b[0])
    .map((q): V2 => [q[0], q[1] + wallAt(q[0], floorStartX, floorSpan, coveWallFloor)]);
  const ceilMid: V2[] = rUpper
    .filter((q) => q[0] > ceilStartX && q[0] < xBack)
    .sort((a, b) => a[0] - b[0])
    .map((q): V2 => [q[0], q[1] - wallAt(q[0], ceilStartX, ceilSpan, coveWallCeil)]);

  // The cove floor/ceiling back-wall landings (offset surfaces sampled at xBack — the deepest point).
  const yLoBack = interp(xBack, rLower.map((q) => q[0]), rLower.map((q) => q[1])) + coveWallFloor;
  const yUpBack = interp(xBack, rUpper.map((q) => q[0]), rUpper.map((q) => q[1])) - coveWallCeil;
  const backGap = Math.abs(yUpBack - yLoBack);
  // Round the cove back wall. The legacy single-corner fillet degenerated when floor & ceiling ran
  // near-parallel (α→π ⇒ radius/tan(α/2)→0 ⇒ a vertical join). Insert a SHORT near-vertical back-wall
  // segment so each side meets it at a real ~90° corner that filletCorner can actually round into a
  // smooth recess front. The back wall is pushed forward to `xNose`; the two corners are filleted
  // independently and their arcs joined by the (now rounded-away) wall.
  const yMid = 0.5 * (yLoBack + yUpBack);
  // Pull the rounded front a little further forward so the recess reads as a clean bowl, scaled by the
  // back-wall gap but capped so it never overruns the cove depth.
  const xNose = xBack + Math.min(0.25 * backGap, 0.3 * coveDepth);
  const floorNose: V2 = [xBack, yLoBack];
  const ceilNose: V2 = [xBack, yUpBack];
  const noseLo: V2 = [xNose, yMid - 0.25 * backGap]; // lower corner of the short back wall
  const noseUp: V2 = [xNose, yMid + 0.25 * backGap]; // upper corner of the short back wall
  const rFloor = Math.max(1e-4, 0.45 * backGap);
  const rCeil = Math.max(1e-4, 0.45 * backGap);
  const roundLo = filletCorner(floorNose, noseLo, noseUp, rFloor, 20); // floor → back wall corner
  const roundUp = filletCorner(noseLo, noseUp, ceilNose, rCeil, 20);   // back wall → ceiling corner

  // Assemble the reflected cove pocket: lower cut face → floor (toward nose) → rounded lower corner →
  // short back wall → rounded upper corner → ceiling (back toward cut) → upper cut face. Reflect every
  // point back (x → -x) before splicing.
  const reflectedPocket: V2[] = [
    loOuter,
    lowerCutTop,
    ...floorMid,
    roundLo.tIn,
    ...roundLo.arc,
    roundLo.tOut,
    ...roundUp.arc,
    roundUp.tOut,
    ...ceilMid.slice().reverse(),
    upInner,
    upOuter,
  ];
  const pocket: V2[] = reflectedPocket.map((p): V2 => [-p[0], p[1]]);

  // Splice the pocket in where the vertical back face would have been. `kept` runs around the aft
  // body and crosses x = cutX twice (once on the lower surface near the lower-cut end, once on the
  // upper). Rebuild the closed contour: walk `kept` until the first cutX crossing on the LOWER side,
  // insert the pocket (which runs lower-cut → nose → upper-cut), then continue from the upper cutX
  // crossing. Identify the two crossings as the vertices exactly at x = cutX.
  const crossingIdx: number[] = [];
  for (let i = 0; i < kept.length; i++) if (Math.abs((kept[i]?.[0] ?? Infinity) - cutX) < 1e-12) crossingIdx.push(i);
  if (crossingIdx.length < 2) {
    // Degenerate (shouldn't happen for a real slat cut): fall back to the blunt trim.
    return trimMainLeadingEdge(contour, cutX);
  }
  const iA = crossingIdx[0]!;
  const iB = crossingIdx[crossingIdx.length - 1]!;
  // kept[iA] and kept[iB] are the two back-wall corners bracketing the trimmed nose. Splice the
  // rounded pocket between them, replacing the vertical face. The pocket runs loOuter → nose → upOuter
  // (lower-crossing first); orient it to match which bracketing crossing is the lower one.
  const before = kept.slice(0, iA + 1);
  const after = kept.slice(iB);
  const interior = pocket.slice(1, -1); // drop duplicate loOuter/upOuter (== the two crossings)
  const aIsLower = (kept[iA]?.[1] ?? 0) <= (kept[iB]?.[1] ?? 0);
  const orientedInterior = aIsLower ? interior : interior.slice().reverse();
  return [...before, ...orientedInterior, ...after];
}

/**
 * PROPER leading-edge slat contour (FXC-9009 LE-device tranche) — replaces the crude
 * {@link buildPlainSlat}. The slat is the part of the main forward of `slat.cutX`, but reshaped into
 * a real slat profile, returned in the STOWED airfoil frame (the caller rotates+translates it by
 * deflection/gap/overlap before render):
 *
 *  - UPPER + LE: the airfoil's TRUE upper surface + rounded LE forward of `cutX` (faithful suction
 *    skin), exactly as {@link buildPlainSlat}'s `upperFwd`.
 *  - AFT/UNDERSIDE: a CONCAVE CUSPED SLOT surface — a clamped cubic B-spline (the flaperon-nose
 *    machinery: {@link evaluateBSpline} + {@link forwardTangent} slope-lock) from the lower-surface
 *    point at `cutX` (slope-locked C1 to the lower skin), pulled UP into the body (the cusp / slot
 *    ceiling, depth `slotDepth`), feathering to a thin TE (`teThickness`) near the LE-cove region.
 *
 * Selig-ordered: upper-cut corner → upper surface → LE → lower surface → cusp B-spline (slot ceiling)
 * → feathered TE, closing the loop. A valid closed contour (≥ 3 pts).
 */
// Slat shape + clamp constants (FXC-9009). The slat is reconceived as a faithful nose with a CONCAVE
// COVE (the slot wall) hooking from a sharp cusp on the lower nose up to a thin feathered tail, tuned
// to the real 30P30N slat (rounded nose, convex upper, deep cove, sharp cusp, long thin tail).
const SLAT_MARGIN = 0.004;      // clearance kept below the suction skin
const SLAT_MESH_FLOOR = 0.0025; // absolute lower bound on the feathered tail the beta mesher tolerates
const SLAT_COVE_K = 0.35;       // main-cove depth as a fraction of local thickness (co-scaled, §6.5)
const SLAT_CUSP_FRAC = 0.30;    // cove cusp station as a fraction of the (effective) slat chord
const SLAT_HOOK_FRAC = 0.30;    // intrinsic cove-hook rise (fraction of nose thickness); slotDepth adds to it
const SLAT_HOOK_DX = 0.12;      // cove-hook handle x-advance, as a fraction of the cove span (steepness)
const SLAT_TAIL_H = 0.55;       // TE handle pull-back along the upper skin (sets the thin faired tail)

/** The render-time resolution of a slat's foil-relative geometry (FXC-9009 §6, 30P30N cove model). */
export interface ResolvedSlat {
  /** Effective chordwise cut. A FLOOR that engages only on a too-sharp nose; == `cutX` on a fat foil. */
  cutXeff: number;
  /** The variant the RENDER uses: the authored variant, or 'sealed' when the foil is too thin to slot. */
  effectiveVariant: 'slotted' | 'sealed' | 'fixed-slot';
  /** Resolved (clamped) tail thickness — authored target floored at the mesh minimum, never re-hashed. */
  teThickRes: number;
  /** The concave cove samples (cusp → thin TE), or `null` when the slat degenerates to a sealed feather. */
  cove: V2[] | null;
  /** Cove cusp station (where the lower nose hands off to the cove) + the upper/lower skin y at the cut. */
  xCusp: number;
  yUpCut: number;
  yLoCut: number;
}

/**
 * Resolve a slat's foil-relative geometry at RENDER time (FXC-9009, 30P30N cove model). The slat is the
 * parent foil's own nose forward of `cutXeff`; its underside is a CONCAVE COVE — a clamped cubic
 * B-spline that leaves a sharp cusp on the lower nose, hooks up steeply, then fairs to a THIN tail along
 * the suction skin (the converging-slot wall). The authored `slotDepth` sets the cove-hook depth and
 * `teThickness` the tail — both clamped DOWN against the MEASURED nose thickness (never scaled up, never
 * re-fingerprinted — D-Codebase-1) so a thin/sharp foil cannot punch through its own suction skin and
 * degenerates to a SEALED feather (D-Aero-2). On the DEFAULT LS(1)-0417 `cutXeff == cutX`.
 */
export function resolveSlat(airfoil: AirfoilCoords, slat: SlatElement, bluntThickness = 0.003): ResolvedSlat {
  const { cutX, slotDepth, teThickness, variant } = slat;
  const upX = airfoil.upper.map((q) => q[0]);
  const upY = airfoil.upper.map((q) => q[1]);
  const loX = airfoil.lower.map((q) => q[0]);
  const loY = airfoil.lower.map((q) => q[1]);
  const { upper: rLEUpper } = noseRadii(airfoil);

  // cutXeff (D-Robust-3): a FLOOR that engages ONLY when the authored cut is below a small multiple of
  // the upper nose radius AND the nose there is too thin to host a cove. On a fat foil neither conjunct
  // holds ⇒ cutXeff == cutX (byte-identity of the resolved cut).
  let cutXeff = cutX;
  const minStation = 1.5 * rLEUpper;
  if (cutX < minStation && interp(cutX, upX, upY) - interp(cutX, loX, loY) < 4 * SLAT_MARGIN) {
    cutXeff = minStation;
  }

  const yUpCut = interp(cutXeff, upX, upY);
  const yLoCut = interp(cutXeff, loX, loY);
  const xCusp = SLAT_CUSP_FRAC * cutXeff;
  const yLoCusp = interp(xCusp, loX, loY);             // the cusp sits on the parent lower nose (continuous)
  const span = Math.max(1e-4, cutXeff - xCusp);
  const noseThick = yUpCut - yLoCusp;                  // available room between cusp and suction skin

  // teThickRes: tail thickness, mesh-floored (never a knife edge — D-Aero-3).
  const teThickRes = Math.max(teThickness, SLAT_MESH_FLOOR, bluntThickness);
  const Tte: V2 = [cutXeff, yUpCut - teThickRes];      // the thin faired tail at the cut

  // Degenerate ⇒ SEALED (D-Aero-2): the nose is too thin to host a cove (the tail would punch through
  // the lower nose, or there is no vertical room for a hook).
  let degenerate = noseThick < 3 * SLAT_MARGIN || Tte[1] <= yLoCusp + SLAT_MARGIN;

  let cove: V2[] | null = null;
  if (!degenerate) {
    // Cove control net (cusp → thin tail), a clamped cubic B-spline. C1 hooks UP steeply out of the
    // cusp (the deep scoop); C2 pulls back along the suction skin so the tail fairs thin (30P30N).
    const cuspPt: V2 = [xCusp, yLoCusp];
    const hookX = xCusp + SLAT_HOOK_DX * span;
    // hook rise: the authored slotDepth target, but never less than an intrinsic 30P30N-like hook and
    // never within `margin` of the suction skin (the HARD clamp — D-Robust-1; thin foils self-seal).
    const hookRoom = interp(hookX, upX, upY) - yLoCusp - SLAT_MARGIN;
    // The authored slotDepth ADDS to an intrinsic 30P30N-like hook, then clamps to the measured room so
    // a deeper authored cove always deepens the scoop (monotone) but never punches the suction skin.
    const hookH = Math.min(SLAT_HOOK_FRAC * noseThick + Math.max(0, slotDepth), hookRoom);
    const C1: V2 = [hookX, yLoCusp + hookH];
    const tHatUp = forwardTangent(upX, upY, cutXeff);                 // -x along the suction skin
    const C2: V2 = add(Tte, scale(tHatUp, SLAT_TAIL_H * span));       // tail handle, parallel to the skin
    const cps = [cuspPt, C1, C2, Tte].map((p, i) => ({ x: p[0], y: p[1], id: String(i) }));
    const samples: V2[] = evaluateBSpline(cps, 3, 40).map((p) => [p.x, p.y]); // cusp → TE

    // Non-punch-through guard: the whole cove must stay strictly below the suction skin. If it doesn't
    // (a too-thin nose the clamps couldn't save), fall back to the sealed feather.
    let ok = hookH > 0;
    if (ok) for (const [x, y] of samples) { if (y > interp(x, upX, upY) - 0.5 * SLAT_MARGIN) { ok = false; break; } }
    if (ok) cove = samples; else degenerate = true;
  }

  const effectiveVariant: 'slotted' | 'sealed' | 'fixed-slot' =
    variant === 'sealed' || degenerate ? 'sealed' : variant;
  return { cutXeff, effectiveVariant, teThickRes, cove, xCusp, yUpCut, yLoCut };
}

export function buildSlat(airfoil: AirfoilCoords, slat: SlatElement, bluntThickness = 0.003): V2[] {
  const r = resolveSlat(airfoil, slat, bluntThickness);
  const { cutXeff, xCusp, yUpCut, teThickRes, cove } = r;
  const loX = airfoil.lower.map((q) => q[0]);
  const loY = airfoil.lower.map((q) => q[1]);

  // Faithful upper/LE skin: cut corner (the tail root) → LE (decreasing x).
  const upperFwd: V2[] = [
    [cutXeff, yUpCut],
    ...airfoil.upper.filter((q) => q[0] < cutXeff).slice().reverse(),
  ];
  // Lower nose: LE → cusp station (the parent lower skin), where the cove hands over.
  const lowerNose: V2[] = airfoil.lower.filter((q) => q[0] > 1e-9 && q[0] < xCusp);

  // The underside: the concave cove (cusp → thin TE), or — when the foil is too thin — a straight thin
  // feather from the cusp up to a tail clamped below the suction skin (SEALED degeneration).
  const cuspPt: V2 = [xCusp, interp(xCusp, loX, loY)];
  const sealedTailY = Math.min(yUpCut - teThickRes, yUpCut - SLAT_MARGIN);
  const underside: V2[] = cove ?? [cuspPt, [cutXeff, sealedTailY]];

  return [
    ...upperFwd,        // cut corner → LE
    ...lowerNose,       // LE → cusp (parent lower nose)
    ...underside,       // cusp → cove/feather → thin TE at the cut (closes to the cut corner)
  ];
}

// ---- Krueger leaf geometry helpers (FXC-9009 rebuild) ------------------------------------------

/** Walk `seq` from its first vertex accumulating Euclidean arc length, returning the prefix whose
 *  total length is `target` (the final segment is cut exactly at `target`). STEP-1 arc walk (§5.1). */
function takeArc(seq: V2[], target: number): V2[] {
  if (seq.length === 0) return [];
  const out: V2[] = [seq[0]!];
  let acc = 0;
  for (let i = 1; i < seq.length; i++) {
    const seg = norm(sub(seq[i]!, seq[i - 1]!));
    if (acc + seg >= target) {
      const f = (target - acc) / Math.max(seg, 1e-12);
      out.push([seq[i - 1]![0] + f * (seq[i]![0] - seq[i - 1]![0]), seq[i - 1]![1] + f * (seq[i]![1] - seq[i - 1]![1])]);
      return out;
    }
    acc += seg;
    out.push(seq[i]!);
  }
  return out;
}

/** True iff OPEN segments p1-p2 and p3-p4 properly cross (shared endpoints / collinear touches do NOT
 *  count — adjacent contour edges always share a vertex). Used by the §5.6/§5.8 closure guards. */
function segmentsCross(p1: V2, p2: V2, p3: V2, p4: V2): boolean {
  const side = (a: V2, b: V2, c: V2): number => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const d1 = side(p3, p4, p1);
  const d2 = side(p3, p4, p2);
  const d3 = side(p1, p2, p3);
  const d4 = side(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/** True iff the CLOSED polyline self-intersects (any non-adjacent edge pair crosses). O(n²) — fine for
 *  the resampled leaf (a few hundred verts). Adjacent + wrap-adjacent edges share a vertex → skipped. */
export function loopSelfIntersects(loop: V2[]): boolean {
  const n = loop.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a1 = loop[i]!;
    const a2 = loop[(i + 1) % n]!;
    for (let k = i + 2; k < n; k++) {
      if (i === 0 && k === n - 1) continue; // wrap-adjacent (edge n-1→0 touches edge 0→1)
      if (segmentsCross(a1, a2, loop[k]!, loop[(k + 1) % n]!)) return true;
    }
  }
  return false;
}

/** True iff two closed loops cross (any edge of `a` properly crosses any edge of `b`). The §5.6 deploy
 *  guard: the deployed panel must not poke THROUGH the main contour (two crossing loops = invalid mesh). */
export function loopsCross(a: V2[], b: V2[]): boolean {
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i]!;
    const a2 = a[(i + 1) % a.length]!;
    for (let k = 0; k < b.length; k++) {
      if (segmentsCross(a1, a2, b[k]!, b[(k + 1) % b.length]!)) return true;
    }
  }
  return false;
}

/** Uniform arc-length resample of a CLOSED loop to `n` vertices (§5.8 wire/3D parity — avoids faceting
 *  on the long cambered span; same arc-length scheme as wingShell.resampleLoop). */
function arcResampleClosed(loop: V2[], n: number): V2[] {
  if (loop.length < 3) return loop.slice();
  const pts = [...loop, loop[0]!]; // close the ring
  const s: number[] = [0];
  for (let i = 1; i < pts.length; i++) s.push(s[i - 1]! + norm(sub(pts[i]!, pts[i - 1]!)));
  const total = s[s.length - 1]! || 1;
  const out: V2[] = [];
  for (let i = 0; i < n; i++) {
    const t = (total * i) / n;
    let j = 0;
    while (j < s.length - 2 && s[j + 1]! < t) j++;
    const seg = s[j + 1]! - s[j]!;
    const f = seg > 1e-12 ? (t - s[j]!) / seg : 0;
    out.push([pts[j]![0] + f * (pts[j + 1]![0] - pts[j]![0]), pts[j]![1] + f * (pts[j + 1]![1] - pts[j]![1])]);
  }
  return out;
}

/** Local radius of curvature at `b` given neighbours `a`,`c` (triangle circumradius). Large ⇒ flat.
 *  Clamps the inner-offset near the small-radius LE wrap so it can't fold into a bowtie cusp (§5.3). */
function curvatureRadius(a: V2, b: V2, c: V2): number {
  const ab = norm(sub(b, a));
  const bc = norm(sub(c, b));
  const ca = norm(sub(a, c));
  const area2 = Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])); // 2·area
  if (area2 < 1e-12) return Infinity; // collinear → flat
  return (ab * bc * ca) / (2 * area2);
}

/**
 * KRUEGER flap contour (FXC-9009 LE-device tranche — REBUILT per the §5 construction algorithm).
 * Mirrors the {@link buildSlat} idiom to produce a real Krueger leaf — NOT the old paper-thin flat
 * plate that never wrapped the LE. In the STOWED airfoil frame (the caller rotates about the hinge,
 * then deploys §5.6). All construction reuses existing primitives ({@link evaluateBSpline},
 * {@link forwardTangent}, {@link filletCorner}); it is NOT a new geometry primitive.
 *
 *  STEP 1 (§5.1) — a suction face that WRAPS the LE: the in-cruise LOWER skin walked from the hinge
 *    forward to x=0 and a short way onto the nose, total arc = `panelArcFraction` (the surface swap —
 *    the in-cruise lower face becomes the deployed UPPER/suction face).
 *  STEP 2 (§5.2) — a FRONT-LOADED CONVEX camber: the face is bowed OUTWARD along the local normal via
 *    a clamped cubic B-spline (the INVERSE of the slat's concave cusp, §0 fact 3), most curvature in
 *    the forward third near the bullnose. `panelCamber` is a fraction of panel ARC (D3).
 *  STEP 3 (§5.3) — a real TAPERED leaf: the inner (pressure) face is a normal offset that swells to a
 *    leading bulge (≈2·`bullnoseRadius`) near the nose and feathers to `panelThickness` at the hinge,
 *    each offset clamped to ≤0.8× the local curvature radius so the LE wrap can't form a cusp.
 *  STEP 4 (§5.4) — a real rounded BULLNOSE (radius `bullnoseRadius`, clamped so filletCorner seats it).
 *  STEP 5 (§5.5) — Selig-ordered closed loop: hinge → suction face → bullnose → inner face → close,
 *    then arc-length resampled (§5.8) for 2D/3D parity with a closed + non-self-intersecting assert.
 *
 * `panelCamber=0` ⇒ a straight skin slice (simple flat Krueger); `bullnoseRadius=0` ⇒ a knife edge.
 * Old persisted elements lacking the rebuilt fields default here (D9: legacy `bullnose` bool → radius).
 * `bluntThickness` is retained only as a non-degeneracy FLOOR on the leaf thickness (NOT coupled, D6).
 */
export function buildKrueger(airfoil: AirfoilCoords, krueger: KruegerElement, bluntThickness: number): V2[] {
  const lowerHingeX = krueger.lowerHingeX;
  // Migration-safe reads (§7.4): old persisted elements lack the rebuilt fields → the SAME defaults
  // makeKruegerElement supplies, and the legacy `bullnose` bool → radius (D9: false→0, else→0.018).
  const panelArc = Math.max(1e-3, krueger.panelArcFraction ?? 0.15);
  const camberFrac = Math.max(0, krueger.panelCamber ?? 0.06);
  const bullnoseRadius = krueger.bullnoseRadius ?? (krueger.bullnose === false ? 0 : 0.018);
  const panelThickness = Math.max(krueger.panelThickness ?? 0.004, bluntThickness); // floor, not coupling (D6)

  const loX = airfoil.lower.map((q) => q[0]);
  const loY = airfoil.lower.map((q) => q[1]);
  const yHinge = interp(lowerHingeX, loX, loY);

  // STEP 1 — walk the lower skin from the hinge FORWARD to the LE (x→0), then WRAP onto the upper nose,
  // accumulating arc length until `panelArc`. The walked in-cruise lower skin (+ a short upper-nose
  // wrap) is the deployed SUCTION face. Ordered hinge → … → LE → … → noseTip (up the upper nose).
  const lowerDesc = airfoil.lower.filter((q) => q[0] < lowerHingeX - 1e-9 && q[0] > 1e-9).slice().reverse();
  const leLower = airfoil.lower[0] ?? [0, 0];                  // ≈ the true LE [0,0]
  const upperWrap = airfoil.upper.filter((q) => q[0] > 1e-9);  // ascending from the LE up the nose
  const walk: V2[] = [[lowerHingeX, yHinge], ...lowerDesc, leLower, ...upperWrap];
  const suctionPts = takeArc(walk, panelArc);
  const nPts = suctionPts.length;
  if (nPts < 3) {
    // Degenerate (panelArc absurdly small): a thin straight stub so downstream consumers stay valid.
    return [[lowerHingeX, yHinge], [lowerHingeX, yHinge - panelThickness], [lowerHingeX - 1e-3, yHinge]];
  }

  // Local OUTWARD normal = the LEFT of travel for the hinge→LE→upper walk winding. This is CONSISTENT
  // (no per-point sign test) — a reference-point sign flip is fragile exactly at the LE where the
  // surface is vertical, and a flip there folds the inner offset into a self-intersecting cusp. By the
  // walk winding, [-t[1], t[0]] is outward everywhere: down on the lower skin, FORWARD at the LE (so a
  // convex bump projects the leaf AHEAD of the wing), up on the upper-nose wrap.
  const tangentAt = (pts: V2[], i: number): V2 => {
    const a = pts[Math.max(0, i - 1)]!;
    const b = pts[Math.min(pts.length - 1, i + 1)]!;
    const t = sub(b, a);
    const len = norm(t);
    return len > 1e-12 ? scale(t, 1 / len) : [1, 0];
  };
  const outwardNormalAt = (pts: V2[], i: number): V2 => {
    const t = tangentAt(pts, i);
    return [-t[1], t[0]];
  };

  // STEP 2 — front-loaded CONVEX camber. Bow each point of the (already dense, smooth) suction skin
  // OUTWARD along its LOCAL normal by camberOffset·bump(s); bump is 0 at the hinge (slope-locked) and 0
  // at the nose tip (clean bullnose seat), peaking in the forward third (s≈2/3, near the nose).
  // camberOffset is a fraction of panel ARC (D3). The slat pulls its cusp CONCAVE (+slotDepth into the
  // body); the Krueger INVERTS to convex/outward along the normal (§0 fact 3, STEP 2). NOTE: we offset
  // the dense skin DIRECTLY rather than fitting a sparse clamped B-spline through it — an approximating
  // cubic overshoots near the hinge and dips the suction face across the inner offset (a self-
  // intersection); the direct normal offset of the smooth skin is the same front-loaded convex camber
  // without that failure mode, and the §5.8 arc-resample smooths the result for wire/3D parity.
  let suctionFace: V2[];
  if (camberFrac > 1e-9) {
    const camberOffset = camberFrac * panelArc;
    const bump = (s: number): number => 6.75 * s * s * (1 - s); // peak≈1 at s≈2/3, 0 at both ends
    suctionFace = suctionPts.map((p, i): V2 => {
      const s = i / (nPts - 1);
      const nrm = outwardNormalAt(suctionPts, i);
      const d = camberOffset * bump(s);
      return [p[0] + nrm[0] * d, p[1] + nrm[1] * d];
    });
  } else {
    suctionFace = suctionPts; // straight skin slice → simple flat Krueger
  }
  const nFace = suctionFace.length;

  // STEP 3 — tapered leaf thickness. The inner (pressure) face is the suction face offset INWARD by a
  // thickness that SWELLS to a leading bulge (≈2·bullnoseRadius, so the §5.4 bullnose fillet seats) near
  // the nose and feathers to `panelThickness` at the hinge. Clamp each local offset to ≤0.6× the local
  // radius of curvature so the small-radius LE wrap can't fold into a bowtie cusp — the discrete
  // 3-point curvature can UNDER-estimate the sharp LE turn, so the §5.8 fallback below is the backstop.
  const noseBulge = bullnoseRadius > 1e-6 ? 2 * bullnoseRadius : Math.max(panelThickness, 2e-3);
  const smooth = (u: number): number => u * u * (3 - 2 * u);
  // The thickness tapers to ~0 at the HINGE ROOT (a clean feathered root — a blunt root edge tangles
  // with the suction face and forces the thin fallback) and SWELLS to the leading bulge (≈2·bullnose
  // radius) at the nose, giving a teardrop leaf with a fat rounded bullnose. The offset is curvature-
  // clamped with a WIDE (±2) stencil — an adjacent 3-pt circumradius is noisy on the dense skin and
  // intermittently over/under-clamps; the wider stencil gives a stable local bend radius.
  const innerOffsets = (faces: V2[], scaleK: number): V2[] => {
    const W = 2;
    return faces.map((p, i): V2 => {
      const s = faces.length > 1 ? i / (faces.length - 1) : 0;
      const want = scaleK * noseBulge * smooth(s);
      const a = faces[Math.max(0, i - W)]!;
      const c = faces[Math.min(faces.length - 1, i + W)]!;
      const off = Math.min(want, 0.6 * curvatureRadius(a, p, c));
      const nrm = outwardNormalAt(faces, i);
      return [p[0] - nrm[0] * off, p[1] - nrm[1] * off]; // inward = -outward
    });
  };

  // STEP 4/5 — close the loop with a real rounded BULLNOSE. Clamp the radius so filletCorner can seat a
  // tangent arc: ≤ 0.5·|innerNose − suctionNose| (else it degenerates to a knife) (§5.4 fillet guard).
  const assemble = (innerFace: V2[]): V2[] => {
    const sNose = suctionFace[nFace - 1]!;
    const iNose = innerFace[nFace - 1]!;
    const r = Math.min(bullnoseRadius, 0.5 * norm(sub(iNose, sNose)));
    const gap = norm(sub(iNose, sNose));
    if (r > 1e-4 && gap > 1e-5) {
      // Push the fillet vertex FORWARD along the nose TANGENT (the leaf's travel direction) — NOT the
      // normal: the nose gap sNose→iNose already lies along the normal, so a normal-pushed vertex is
      // collinear with both faces (alpha→π ⇒ filletCorner's unit(v1+v2) → NaN). The tangent gives a
      // real wedge the arc seats into, reading as a D-shaped bulb.
      const tNose = tangentAt(suctionFace, nFace - 1);
      const mid: V2 = [0.5 * (sNose[0] + iNose[0]), 0.5 * (sNose[1] + iNose[1])];
      const vertex: V2 = [mid[0] + tNose[0] * gap, mid[1] + tNose[1] * gap];
      const round = filletCorner(sNose, vertex, iNose, r, 24);
      return [
        ...suctionFace.slice(0, -1),       // hinge → just before nose
        round.tIn,
        ...round.arc,                      // rounded D-bullnose
        round.tOut,
        ...innerFace.slice(0, -1).reverse(), // nose → hinge (inner/pressure face)
      ];
    }
    // Knife edge (simple Krueger, bullnoseRadius=0): the suction nose meets the inner nose at a point.
    return [...suctionFace, ...innerFace.slice().reverse()];
  };

  // STEP 8 — build + arc-length resample (§5.8, 240 verts: keeps the bullnose arc well-sampled vs the
  // long cambered span without faceting; 2D-canvas / 3D-loft parity), then a HARD closed + non-self-
  // intersecting assert (D10). If an aggressive camber+thickness at a small arc still folds, progressively
  // shrink the inner offset and rebuild; the last resort is a thin knife leaf, which is always simple.
  const buildAt = (scaleK: number): V2[] => arcResampleClosed(assemble(innerOffsets(suctionFace, scaleK)), 240);
  let loop = buildAt(1);
  for (const k of [0.6, 0.35, 0.2]) {
    if (!loopSelfIntersects(loop)) return loop;
    loop = buildAt(k);
  }
  if (!loopSelfIntersects(loop)) return loop;
  const thin: V2[] = suctionFace.map((p, i): V2 => {
    const nrm = outwardNormalAt(suctionFace, i);
    return [p[0] - nrm[0] * 0.5 * panelThickness, p[1] - nrm[1] * 0.5 * panelThickness];
  });
  return arcResampleClosed([...suctionFace, ...thin.slice().reverse()], 240);
}

/**
 * DROOP NOSE / variable-camber LE morph (FXC-9009 LE-device tranche). NOT a separate element — a
 * continuous morph of the main airfoil: every surface point forward of `hingeX` is rotated DOWNWARD
 * about the hinge `[hingeX, camberMidpointY(coords, hingeX)]` by an angle that RAMPS from 0 at the
 * hinge to `droopDeg` at the LE (a smoothstep blend), so the contour stays C1 at the hinge (no kink).
 * Points aft of `hingeX` are untouched. Returns a morphed {@link AirfoilCoords} that flows straight
 * into {@link buildMainWing} (and the cove/TE/flap build off the drooped coords unchanged).
 *
 * POSITIVE `droopDeg` = nose DOWN (clockwise in the x-aft/y-up frame ⇒ negative-CCW). `droopDeg === 0`
 * is the IDENTITY (the default config stays byte-identical).
 */
export function droopNose(coords: AirfoilCoords, hingeX: number, droopDeg: number): AirfoilCoords {
  if (!Number.isFinite(droopDeg) || droopDeg === 0 || !Number.isFinite(hingeX) || hingeX <= 0) {
    return coords;
  }
  const center: V2 = [hingeX, camberMidpointY(coords, hingeX)];
  // The nose sits FORWARD of (left of) the hinge, so a CCW (positive) rotation drops it DOWN — the
  // opposite hand to a TE-down flap (whose aft surface is right of its hinge). POSITIVE droopDeg ⇒
  // nose down ⇒ positive (CCW) angle here.
  const maxRad = (droopDeg * Math.PI) / 180;
  // Smoothstep ramp: 0 at the hinge (x = hingeX) → 1 at the LE (x = 0). weight(t) = 3t²−2t³.
  const morph = (pts: V2[]): V2[] =>
    pts.map((p): V2 => {
      if (p[0] >= hingeX) return p;                       // aft of the hinge: untouched
      const t = Math.min(1, Math.max(0, (hingeX - p[0]) / hingeX)); // 0 at hinge → 1 at LE
      const w = t * t * (3 - 2 * t);                      // smoothstep
      const a = maxRad * w;
      const c = Math.cos(a), s = Math.sin(a);
      const dx = p[0] - center[0];
      const dy = p[1] - center[1];
      return [dx * c - dy * s + center[0], dx * s + dy * c + center[1]];
    });
  return { upper: morph(coords.upper), lower: morph(coords.lower) };
}

/** The vane's 5 logical control points: [te, ...points] (the TE + 4 others). */
export function vaneControlPoints(shape: VaneShape): V2[] {
  return [shape.te, ...shape.points];
}

/**
 * Vane contour: an open clamped cubic B-spline. The TE location is split into the
 * spline's start and end points by the blunt-TE thickness, added orthogonal to the
 * averaged direction of the TE's two adjacent control points (`points[0]` and
 * `points[3]`); the 4 control points shape the loop in between. The spline passes
 * through the two TE corners (gap = `bluntThickness`) and approximates the 4 points.
 * Selig-ordered: TE-corner → P1 → … → P4 → other-TE-corner.
 */
export function buildVane(shape: VaneShape, bluntThickness: number): V2[] {
  const { te, points } = shape;
  const p1 = points[0] ?? te, p2 = points[1] ?? te, p3 = points[2] ?? te, p4 = points[3] ?? te;
  // averaged direction toward the two control points adjacent to the TE
  const dir = unit(add(unit(sub(p1, te)), unit(sub(p4, te))));
  let n: V2 = [-dir[1], dir[0]];                       // orthogonal to that direction
  if (dot(sub(p1, te), n) < 0) n = [-n[0], -n[1]];     // orient toward p1's side
  const h = bluntThickness / 2;
  const teStart: V2 = [te[0] + h * n[0], te[1] + h * n[1]];   // start (adjacent to p1)
  const teEnd: V2 = [te[0] - h * n[0], te[1] - h * n[1]];     // end (adjacent to p4)
  const cps = [teStart, p1, p2, p3, p4, teEnd].map((p, i) => ({ x: p[0], y: p[1], id: String(i) }));
  return evaluateBSpline(cps, 3, 81).map((q) => [q.x, q.y]);
}

// ---------------------------------------------------------------------------
// Deployment track path + motion law
// ---------------------------------------------------------------------------

/**
 * Sample the deploy track as a dense polyline (stowed pivot → carriage waypoints) and return
 * an arc-length parameterization. With no waypoints the track is a single point (zero length):
 * every fraction maps back to the pivot, so {@link pivotAtFraction} is constant and the whole
 * mechanism collapses to single-pivot rotation. The track is a clamped cubic B-spline through
 * [pivot, ...waypoints] (degree clamps down automatically for short control nets via the
 * bspline lib), matching how the vane/flaperon noses are built.
 */
export interface SampledTrack {
  /** Densely sampled track points (global frame). At least one point (the pivot). */
  pts: V2[];
  /** Cumulative arc length at each `pts` sample; `cum[0] = 0`. */
  cum: number[];
  /** Total track arc length (chords). 0 when the track is degenerate. */
  length: number;
}

export function sampleTrack(track: DeployTrack, nSamples = 64): SampledTrack {
  const ctrl: V2[] = [track.pivot, ...(track.waypoints ?? [])];
  if (ctrl.length < 2) {
    return { pts: [track.pivot], cum: [0], length: 0 };
  }
  const cps = ctrl.map((p, i) => ({ x: p[0], y: p[1], id: String(i) }));
  const pts: V2[] = evaluateBSpline(cps, 3, nSamples).map((p) => [p.x, p.y]);
  const cum = [0];
  for (let i = 1; i < pts.length; i++) {
    const cur = pts[i], prev = pts[i - 1];
    if (!cur || !prev) continue;
    cum.push((cum[i - 1] ?? 0) + norm(sub(cur, prev)));
  }
  return { pts, cum, length: cum[cum.length - 1] ?? 0 };
}

/**
 * The motion-law translation schedule g(f): fraction of full track travel reached at deploy
 * fraction f∈[0,1]. 'linear' ⇒ f; 'fowler'/'rotateFirst' ⇒ f^exp (exp>1 delays travel, exp<1
 * front-loads it). Clamped to [0,1].
 */
export function translationSchedule(motion: MotionLaw, fraction: number): number {
  const f = Math.min(1, Math.max(0, fraction));
  if (motion.translationCurve === 'linear') return f;
  const exp = motion.translationExponent > 0 ? motion.translationExponent : 1;
  return Math.pow(f, exp);
}

/** Point on a sampled track at arc length `s` (clamped to the track ends). */
function pointAtArcLength(track: SampledTrack, s: number): V2 {
  const { pts, cum, length } = track;
  const first = pts[0] ?? [0, 0];
  const last = pts[pts.length - 1] ?? first;
  if (pts.length === 1 || length === 0) return first;
  if (s <= 0) return first;
  if (s >= length) return last;
  let i = 1;
  while (i < cum.length && (cum[i] ?? 0) < s) i++;
  const prev = pts[i - 1] ?? first;
  const cur = pts[i] ?? last;
  const cumPrev = cum[i - 1] ?? 0, cumCur = cum[i] ?? cumPrev;
  const t = (s - cumPrev) / (cumCur - cumPrev);
  return add(prev, scale(sub(cur, prev), t));
}

/**
 * The deploy pivot location at deploy fraction f: the stowed pivot translated along the track by
 * `motion.trackTravel·g(f)`. Degenerate track (or zero travel) ⇒ always the stowed pivot.
 */
export function pivotAtFraction(track: SampledTrack, motion: MotionLaw, fraction: number): V2 {
  const travel = motion.trackTravel * translationSchedule(motion, fraction);
  return pointAtArcLength(track, travel);
}

/**
 * Build the deploy transform for the vane+flaperon assembly at a given deploy angle, honouring the
 * track + motion law. The transform is a rigid-body motion: translate the assembly so its stowed
 * pivot lands at `pivotAtFraction(f)`, then rotate by the deploy angle about that translated pivot
 * (TE-down positive ⇒ negative-CCW). Concretely T(p) = R(p − pivot0) + pivot_f, which:
 *   - preserves all intra-assembly distances (rigid), and
 *   - reduces EXACTLY to `rotate(·, deployRad, pivot0)` when there is no track travel
 *     (pivot_f === pivot0), so the degenerate default matches the legacy single-pivot path.
 */
export function deployTransform(
  track: SampledTrack,
  motion: MotionLaw,
  deployAngleDeg: number,
): (pts: V2[]) => V2[] {
  const full = motion.fullDeployAngleDeg !== 0 ? motion.fullDeployAngleDeg : 1;
  const fraction = deployAngleDeg / full;
  const pivot0 = track.pts[0] ?? [0, 0];
  const pivotF = pivotAtFraction(track, motion, fraction);
  const deployRad = -(deployAngleDeg * Math.PI) / 180.0;
  const c = Math.cos(deployRad);
  const s = Math.sin(deployRad);
  return (pts: V2[]): V2[] =>
    pts.map(([x, y]) => {
      const dx = x - pivot0[0];
      const dy = y - pivot0[1];
      return [dx * c - dy * s + pivotF[0], dx * s + dy * c + pivotF[1]] as V2;
    });
}

/**
 * The vane FOLLOWER transform (FXC-9009 double-slotted tranche): the vane rides the SAME deploy
 * `track`/`motion` as the flap, but as a follower — it rotates by `gain`·deployAngle (gain < 1 ⇒
 * deploys less than the flap) and its carriage lags `lagChords` of track arc-length BEHIND the
 * flap's, so the vane trails a slot-gap behind the flap nose instead of moving rigidly with it.
 *
 * The flap's carriage arc-length (what it would reach at this deploy angle) drives the lag datum;
 * the vane sits `lagChords` short of it (clamped at the stowed pivot). With `gain = 1` and
 * `lagChords = 0` this reduces EXACTLY to {@link deployTransform} — same angle, same pivot arc —
 * so the legacy rigid assembly is byte-identical (the identity follower is a no-op).
 */
export function followerTransform(
  track: SampledTrack,
  motion: MotionLaw,
  deployAngleDeg: number,
  gain: number,
  lagChords: number,
): (pts: V2[]) => V2[] {
  const full = motion.fullDeployAngleDeg !== 0 ? motion.fullDeployAngleDeg : 1;
  const flapFraction = deployAngleDeg / full;
  // The flap carriage's arc-length position; the vane trails it by lagChords.
  const flapTravel = motion.trackTravel * translationSchedule(motion, flapFraction);
  const vaneTravel = Math.max(0, flapTravel - lagChords);
  const pivot0 = track.pts[0] ?? [0, 0];
  const pivotF = pointAtArcLength(track, vaneTravel);
  const vaneAngleRad = -(gain * deployAngleDeg * Math.PI) / 180.0;
  const c = Math.cos(vaneAngleRad);
  const s = Math.sin(vaneAngleRad);
  return (pts: V2[]): V2[] =>
    pts.map(([x, y]) => {
      const dx = x - pivot0[0];
      const dy = y - pivot0[1];
      return [dx * c - dy * s + pivotF[0], dx * s + dy * c + pivotF[1]] as V2;
    });
}

/** Resolve the effective track + motion from a design, defaulting to the degenerate single-pivot
 *  description (track at `deployHinge.pivot`, {@link PURE_ROTATION_MOTION}) for legacy configs. */
export function resolveDeploy(cfg: HighLiftAirfoil): { track: SampledTrack; motion: MotionLaw } {
  const d = cfg.design;
  const track: DeployTrack = d.deployTrack ?? { pivot: d.deployHinge.pivot, waypoints: [] };
  const motion: MotionLaw = d.motion ?? PURE_ROTATION_MOTION;
  return { track: sampleTrack(track), motion };
}

// ---------------------------------------------------------------------------
// Full configuration assembly
// ---------------------------------------------------------------------------

/** One deployed element ready to render — its closed contour + (optional) control net. */
export interface DeployedElement {
  /** Stable element id (matches the source {@link Element}.id). */
  id: string;
  kind: ElementKind;
  label: string;
  /** Closed contour at the current deployment. */
  contour: V2[];
  /** B-spline control net at the current deployment (empty for the main element). */
  controls: V2[];
}

export interface Configuration {
  /** Main element contour (fixed; cove cut). */
  main: V2[];
  /** Vane contour at the current deployment. */
  vane: V2[];
  /** The 5 vane B-spline control points, carried to the current deployment. */
  vaneControls: V2[];
  /** Flaperon contour at the current deployment. */
  flaperon: V2[];
  /** Flaperon hinge pivot, carried to the current deployment. */
  flaperonPivot: V2;
  /** The 5 flaperon nose control points, carried to the current deployment. */
  flaperonControls: V2[];
  /** EVERY deployed element (incl. extras beyond the default triple), in collection order. The
   *  legacy `main`/`vane`/`flaperon` fields above are the first of each kind (back-compat); the
   *  renderer iterates this list so added foils show. */
  elements: DeployedElement[];
}

/**
 * Build the element contours for the configuration's operating point. The main element is
 * fixed; the vane and flaperon deploy together as one rigid assembly. The assembly follows the
 * deploy TRACK PATH + MOTION LAW ({@link resolveDeploy}): at deploy fraction f = deployAngleDeg /
 * motion.fullDeployAngleDeg the assembly translates along the track (Fowler aft+down motion) and
 * rotates by `operation.deployAngleDeg` about the translated pivot (0 = stowed). With a degenerate
 * track (no waypoints / zero travel — the legacy default) this is exactly single-pivot rotation
 * about `deployHinge.pivot`. The flaperon is additionally rotated about its own hinge (relative to
 * the assembly), then carried by the deploy transform along with its pivot. All elements share the
 * blunt TE thickness (`main.bluntThickness`).
 *
 * Aerospace sign convention: positive deploy/flaperon angle = trailing edge DOWN. The airfoil frame
 * is x aft / y up, so TE-down is a clockwise (negative-CCW) rotation ⇒ negated inside the transforms.
 */
export function buildConfiguration(cfg: HighLiftAirfoil): Configuration {
  const { main: m, design: d, operation: op } = cfg;
  // Condition the (arbitrary) foil to the exact split/normalised contract, THEN spline-smooth the
  // coarse table once; the main + flaperon-aft surfaces (and so the rendered contour and the RANS
  // mesh boundary) follow the smooth curve, not facets. `conditionFoil` is identity on the DEFAULT
  // LS(1)-0417 (already canonical) so this stays byte-identical (FXC-9009 §4 / D-Codebase-1).
  let coords = densifyAirfoil(conditionFoil(m.coords));

  // DROOP NOSE / variable-camber LE (FXC-9009 LE-device tranche): if a main-cove carries a non-zero
  // `droopDeg`, morph the nose DOWNWARD here so ALL downstream builders (main, flaperon, slat-cove)
  // see the drooped contour. `droopDeg === 0` / absent ⇒ identity ⇒ the default config is byte-identical.
  const firstMainCove = elementsOf(cfg).find((e): e is Extract<typeof e, { kind: 'main-cove' }> => e.kind === 'main-cove');
  if (firstMainCove && Number.isFinite(firstMainCove.droopDeg) && (firstMainCove.droopDeg ?? 0) !== 0) {
    coords = droopNose(coords, firstMainCove.droopHingeX ?? 0.12, firstMainCove.droopDeg ?? 0);
  }

  // Deploy transform from the track + motion law (degenerate track ⇒ legacy fixed-pivot rotation).
  const { track, motion } = resolveDeploy(cfg);
  const move = deployTransform(track, motion, op.deployAngleDeg);
  // The vane's transform: rigid assembly (== `move`) unless the design declares a FOLLOWER, in
  // which case the vane rides the same track at reduced gain + an arc-length lag behind the flap.
  // Identity follower (gain 1 / lag 0) === `move`, so an explicit identity is byte-identical too.
  const vf = d.vaneFollower;
  const vaneMove =
    vf && (vf.gain !== 1 || vf.lagChords !== 0)
      ? followerTransform(track, motion, op.deployAngleDeg, vf.gain, vf.lagChords)
      : move;
  // The flaperon is first rotated about its own hinge (relative to the assembly), then carried by
  // the assembly's deploy transform (which also carries the flaperon pivot).
  const fPivot = d.flaperonHinge.pivot;
  const flapRad = -(op.flaperonAngleDeg * Math.PI) / 180.0;
  const deploy = (pts: V2[]): V2[] => move(rotate(pts, flapRad, fPivot));

  // Iterate the canonical element collection (FXC-9009 §B.5). For the default 3-element section the
  // collection is exactly [main-cove, vane-bspline, flaperon-derived], so this reproduces the
  // legacy fixed-triple output byte-for-byte. The {@link Configuration} fields are keyed by the
  // legacy roles (main / vane / flaperon) — the FIRST element of each kind populates them; extra
  // elements (a 2nd vane etc.) are a later-tranche concern and don't yet flow into the contours
  // here (the multi-element wire lands with the add/move UI).
  // Build EVERY element in the collection (not just the first of each kind) so added foils render.
  // For the default 3-element section this yields exactly [main-cove, vane-bspline, flaperon-derived]
  // with byte-identical contours to the legacy fixed-triple output.
  const allEls = elementsOf(cfg);
  // A plain flap cuts its OWN cove into the main at its hinge. When a plain flap is present we
  // re-cut the (first) main-cove element with the flap's cove so the main is truncated at the hinge
  // and the flap mates that cove (the flap is the cove-and-gap mechanism mirrored to the TE). With
  // no plain flap the main keeps its own (legacy) cove byte-for-byte. A slat additionally trims the
  // main's leading edge forward of `cutX`.
  const firstPlainFlap = allEls.find((e): e is Extract<typeof e, { kind: 'plain-flap' }> => e.kind === 'plain-flap');
  // The plain flap consumes the operation's flaperon knob (op.flaperonAngleDeg) as its motion DOF
  // ONLY when it owns that axis — i.e. no slotted flaperon/vane exists (the same ownership rule as
  // `animateMotionAxis` in motionGapCheck.ts). In a MIXED design the knob belongs to the
  // flaperon-derived element (rotated via `deploy` above); feeding it to the plain flap too would
  // rotate BOTH surfaces from the one DOF. The plain flap then holds its rigged `deflectionDeg`.
  const hasDeployDevice = allEls.some((e) => e.kind === 'vane-bspline' || e.kind === 'flaperon-derived');
  const plainFlapKnobDeg = hasDeployDevice ? 0 : op.flaperonAngleDeg;
  const firstPlainSlat = allEls.find((e): e is Extract<typeof e, { kind: 'plain-slat' }> => e.kind === 'plain-slat');
  // The PROPER slat (FXC-9009 LE-device tranche): carves a rounded LE cove into the main (slotted /
  // fixed-slot) or a blunt trim (sealed). A Krueger re-cuts the main's LOWER LE forward of its hinge.
  const firstSlat = allEls.find((e): e is Extract<typeof e, { kind: 'slat' }> => e.kind === 'slat');
  // Resolve the (first) proper slat ONCE so the main-side cove/trim and the deploy rig agree on the
  // effective variant (a thin foil degenerates to sealed) and the effective cut (§6.1). Null with no slat.
  const resolvedSlat = firstSlat ? resolveSlat(coords, firstSlat, m.bluntThickness) : null;
  const firstKrueger = allEls.find((e): e is Extract<typeof e, { kind: 'krueger' }> => e.kind === 'krueger');
  const buildMainContour = (el?: MainCoveElement): V2[] => {
    // If a plain flap is present, re-cut the main with the flap's TE cove. If a slat is present,
    // trim the main's leading edge forward of the slat cut. Both are gap-free byte-identical
    // no-ops when the respective control surface is absent.
    const coveForMain = firstPlainFlap ? firstPlainFlap.cove : (el?.cove ?? d.cove);
    // Aileron-up: a NEGATIVE plain-flap deflection reflexes the flap up, so the main must
    // present the mirrored (lower-surface) cove rather than the TE-down cove. coveSide stays +1
    // (today's TE-down cove) with no flap or a TE-down deflection, keeping the default byte-identical.
    // A HINGE-CONCENTRIC flap NEVER mirrors: its spiral cove wraps from the lower lip around the front
    // to the upper lip, so it clears the round nose on BOTH up and down deflection about the same hinge
    // — mirroring would build the cove about a y-flipped hinge that no longer matches the nose.
    // The EFFECTIVE deflection is the flap's rigged `deflectionDeg` PLUS the operation's flaperon
    // knob (op.flaperonAngleDeg), which drives the plain flap as its control-surface motion DOF (see
    // the plain-flap case below). At the rigged/stowed operating point (flaperonAngleDeg === 0) this
    // is exactly `deflectionDeg`, so the default/seeded contour stays byte-identical.
    const coveSide: 1 | -1 =
      firstPlainFlap && !firstPlainFlap.concentricNose && firstPlainFlap.deflectionDeg + plainFlapKnobDeg < 0 ? -1 : 1;
    let mainContour = buildMainWing(coords, coveForMain, m.bluntThickness, op.spoilerAngleDeg, coveSide);
    // PROPER slat: carves a ROUNDED leading-edge cove into the main where the slat nests so the
    // slat's cusped underside + this cove form the converging SLOT (slotted / fixed-slot). A
    // SEALED slat has no slot, so the main uses the blunt vertical trim instead.
    if (firstSlat && resolvedSlat) {
      // Use the RESOLVED cut + variant: a thin foil whose clamp went non-positive renders sealed
      // (blunt trim) regardless of the authored enum (D-Aero-2). The cove depth is co-scaled to the
      // local thickness at the cut (D-Codebase-2); on LS(1)-0417 it resolves to the legacy 0.045.
      const sCutX = resolvedSlat.cutXeff;
      const tLocal =
        interp(sCutX, coords.upper.map((q) => q[0]), coords.upper.map((q) => q[1])) -
        interp(sCutX, coords.lower.map((q) => q[0]), coords.lower.map((q) => q[1]));
      const coveDepth = Math.min(SLAT_COVE_K * tLocal, 0.45 * sCutX);
      mainContour =
        resolvedSlat.effectiveVariant === 'sealed'
          ? trimMainLeadingEdge(mainContour, sCutX)
          : buildLeadingEdgeCove(mainContour, coords, sCutX, m.bluntThickness, { coveDepth });
    } else if (firstPlainSlat) {
      // Legacy crude slat (deprecated): the old blunt/cove trim path, kept for back-compat.
      mainContour = buildLeadingEdgeCove(mainContour, coords, firstPlainSlat.cutX, m.bluntThickness);
    }
    // Krueger: the panel is the main's LOWER-LE skin forward of its hinge, so the main loses that
    // skin — carve a ROUNDED leading-edge cove into the main at the Krueger's noseCutX (the same
    // shaped recess as the slotted slat, not the blunt vertical trim) so the panel folds out of a
    // clean rounded pocket. buildLeadingEdgeCove keeps the upper-nose LE (vertices x ≥ cutX + the
    // upper skin LE→cutX) and self-falls-back to the blunt trim on a degenerate cut.
    // LOWER-biased recess (§5.7): a Krueger vacates the LOWER skin and KEEPS the laminar upper LE,
    // so the cove must scoop the FLOOR deep / CEILING shallow (the mirror of the slat's throat) —
    // `lowerBiased=true`. The recess back wall is keyed at the Krueger's `noseCutX` (D7: a single
    // knob keys the recess, not two independent defaults). Slat call sites stay lowerBiased=false.
    if (firstKrueger) mainContour = buildLeadingEdgeCove(mainContour, coords, firstKrueger.noseCutX, m.bluntThickness, { lowerBiased: true });
    return mainContour;
  };

  const deployed: DeployedElement[] = [];
  for (const el of allEls) {
    switch (el.kind) {
      case 'main-cove': {
        deployed.push({
          id: el.id, kind: el.kind, label: el.label,
          contour: buildMainContour(el),
          controls: [],
        });
        break;
      }
      case 'vane-bspline':
        deployed.push({
          id: el.id, kind: el.kind, label: el.label,
          // The vane rides `vaneMove` — the rigid assembly by default, or the follower linkage
          // (reduced gain + arc-length lag behind the flap) when the design declares one.
          contour: vaneMove(buildVane(el.shape, m.bluntThickness)),
          controls: vaneMove(vaneControlPoints(el.shape)),
        });
        break;
      case 'flaperon-derived':
        deployed.push({
          id: el.id, kind: el.kind, label: el.label,
          // FRISE variant: a lower-nose beak ahead of the hinge that swings below the wing on TE-up
          // deflection. `frise` off ⇒ buildFlaperonWithFrise === buildFlaperon (byte-identical).
          contour: deploy(buildFlaperonWithFrise(coords, el.shape, m.bluntThickness, el.frise, el.friseHornFraction ?? 0.03)),
          controls: deploy(flaperonControlPoints(coords, el.shape)),
        });
        break;
      case 'plain-flap': {
        // The aft flap piece, rotated about the hinge by the per-element deflection. POSITIVE
        // deflection = TE down; the airfoil frame is x-aft/y-up, so TE-down is a clockwise
        // (negative-CCW) rotation ⇒ negate (matches the deploy/flaperon sign convention). A NEGATIVE
        // deflection is an aileron-up reflex — the same rotation with the opposite sign falls out.
        const hinge: V2 = [el.hingeX, el.hingeY ?? camberMidpointY(coords, el.hingeX)];
        // The plain flap consumes the operation's flaperon knob (via plainFlapKnobDeg — zero when a
        // slotted flaperon/vane owns the axis) as its control-surface motion DOF, RELATIVE to its
        // rigged `deflectionDeg`. A seeded aileron rigs at `deflectionDeg` and the "animate"/
        // range-of-motion sweep then moves it about that pose via the flaperon axis (the deploy track
        // is inert for a plain-flap design). flaperonAngleDeg === 0 ⇒ the effective angle is exactly
        // `deflectionDeg` ⇒ the default/seeded contour is byte-identical.
        const flapRad = -((el.deflectionDeg + plainFlapKnobDeg) * Math.PI) / 180.0;
        // HINGE-CONCENTRIC nose (opt-in, the internally-hinged sealed aileron — aircraft-CS seed): the
        // forward profile is a circular arc about the SAME hinge (radius R), so with the main cove
        // carved concentric (radius R + gap) the slot clearance stays exactly `gap` through the whole
        // rotation. `concentricNose` off ⇒ the FRISE/B-spline path below is byte-identical.
        // FRISE variant: a lower-nose beak ahead of the hinge (no-op when `frise` is off ⇒ identical
        // to buildPlainFlap). On aileron-up (negative deflection) the horn rotates down below the wing.
        const stowed = el.concentricNose === true && el.cove.arc
          ? buildConcentricFlap(coords, el.shape, m.bluntThickness, el.cove.arc)
          : buildFlaperonWithFrise(coords, el.shape, m.bluntThickness, el.frise, el.friseHornFraction ?? 0.03);
        deployed.push({
          id: el.id, kind: el.kind, label: el.label,
          contour: rotate(stowed, flapRad, hinge),
          controls: rotate(flaperonControlPoints(coords, el.shape), flapRad, hinge),
        });
        break;
      }
      case 'plain-slat': {
        // The slat is the main's leading-edge piece, rotated about the hinge by the droop. POSITIVE
        // deflection = LE down/forward (the deployed slat) — clockwise in the x-aft/y-up frame.
        const hinge: V2 = [el.hingeX, el.hingeY ?? camberMidpointY(coords, el.hingeX)];
        const slatRad = -(el.deflectionDeg * Math.PI) / 180.0;
        const stowed = buildPlainSlat(coords, el.cutX);
        deployed.push({
          id: el.id, kind: el.kind, label: el.label,
          contour: rotate(stowed, slatRad, hinge),
          controls: [],
        });
        break;
      }
      case 'slat': {
        // PROPER slat (FXC-9009 LE-device tranche): a shaped slat with a cusped underside, deployed by
        // rotating about the hinge by the deflection (LE down/forward), then translated by the rigged
        // gap/overlap (forward by `overlap` along -x, down by `gap` along -y) — like the flaperon slot.
        // SEALED ⇒ gap collapses to 0 and the slat is pushed aft so its feathered TE seals onto the
        // main cove (overlap closes the slot). Same sign convention as the legacy slat (slatRad < 0).
        const hinge: V2 = [el.hingeX, el.hingeY ?? camberMidpointY(coords, el.hingeX)];
        const slatRad = -(el.deflectionDeg * Math.PI) / 180.0;
        // Resolve THIS slat's effective variant so the rig agrees with the slat contour + main side: a
        // thin foil whose clamp seals it gets no translation (its feather seals onto the blunt-trimmed
        // main LE) regardless of the authored enum (D-Aero-2).
        const rs = el === firstSlat && resolvedSlat ? resolvedSlat : resolveSlat(coords, el, m.bluntThickness);
        const stowed = buildSlat(coords, el, m.bluntThickness);
        const rotated = rotate(stowed, slatRad, hinge);
        // Rig the slot. SEALED ⇒ no translation: the rotated slat sits flush against the main's
        // blunt-trimmed LE (zero gap). SLOTTED / FIXED-SLOT ⇒ translate the slat FORWARD (-x by
        // `overlap`) and DOWN+FORWARD off the main by `gap` (normal-ish throat) to open the converging
        // slot. Larger `gap` opens the throat further (the riggable knob).
        const dx = rs.effectiveVariant === 'sealed' ? 0 : -(el.overlap + el.gap);
        const dy = rs.effectiveVariant === 'sealed' ? 0 : -el.gap;
        const contour = rotated.map((p): V2 => [p[0] + dx, p[1] + dy]);
        deployed.push({
          id: el.id, kind: el.kind, label: el.label,
          contour,
          controls: [],
        });
        break;
      }
      case 'krueger': {
        // KRUEGER (FXC-9009 LE-device tranche, REBUILT): the lower-LE leaf folds DOWN+FORWARD about its
        // hinge, projecting the bullnose AHEAD of the clean LE (the in-cruise lower skin becomes the
        // deployed suction face). The rebuilt leaf wraps the LE and lies FORWARD/above the lower-nose
        // hinge, so "down/forward" is a POSITIVE (CCW) rotation here — the OPPOSITE sign to the old
        // aft-strip plate (a negative/CW rotation pointed the rebuilt bullnose AFT, a §2.3 wrong
        // rendering). At 65° this projects the tip ≈6%c ahead of the LE (D1 sanity check). NOTE: a
        // single rigid rotation is an IDEALISED single-DOF schedule (D2) — the full ~130° compound
        // deploy + folding-bullnose 2nd DOF is deferred (OQ-2); v1 does not advertise VC fidelity.
        const hinge: V2 = [el.lowerHingeX, el.hingeY ?? interp(el.lowerHingeX, coords.lower.map((q) => q[0]), coords.lower.map((q) => q[1]))];
        const krugRad = (el.deflectionDeg * Math.PI) / 180.0;
        const stowed = buildKrueger(coords, el, m.bluntThickness);
        const rotated = rotate(stowed, krugRad, hinge);
        // §5.6 deploy rig. SEALED (default, the primary v1 deliverable, D4): no translation — the leaf
        // forward edge meets the fixed lower-nose recess lip (the lower-biased cove, §5.7). SLOTTED
        // (experimental, D5/OQ-3): open a converging throat by standing the leaf off by `gap` (down/
        // forward) + `overlap` (forward); this is an APPROXIMATE v1, not a faithful lip-shaped former.
        // EITHER WAY the self-intersection clamp (D10) guarantees the deployed leaf never pokes THROUGH
        // the main contour: the standoff is halved until the panel loop clears the (already-deployed) main.
        const variant = el.variant ?? 'sealed';
        let contour = rotated;
        if (variant === 'slotted') {
          const mainNow = deployed.find((e) => e.kind === 'main-cove')?.contour ?? buildMainContour(firstMainCove);
          const gap = Math.max(0, el.gap ?? 0);
          const overlap = Math.max(0, el.overlap ?? 0);
          let frac = 1;
          for (let i = 0; i < 7; i++) {
            const dx = -frac * (overlap + gap);
            const dy = -frac * gap;
            const trial = rotated.map((p): V2 => [p[0] + dx, p[1] + dy]);
            if (mainNow.length < 3 || !loopsCross(trial, mainNow)) { contour = trial; break; }
            frac *= 0.5; // back off toward the (crossing-free) sealed pose
          }
        }
        deployed.push({
          id: el.id, kind: el.kind, label: el.label,
          contour,
          controls: [],
        });
        break;
      }
    }
  }

  // Legacy single-role fields = the FIRST element of each kind (back-compat + golden). Defensive
  // fallback for the main so the contour is never empty if a malformed collection omits it.
  const firstMain = deployed.find((e) => e.kind === 'main-cove');
  const firstVane = deployed.find((e) => e.kind === 'vane-bspline');
  const firstFlap = deployed.find((e) => e.kind === 'flaperon-derived');

  return {
    main: firstMain?.contour ?? buildMainWing(coords, d.cove, m.bluntThickness, op.spoilerAngleDeg),
    vane: firstVane?.contour ?? [],
    vaneControls: firstVane?.controls ?? [],
    flaperon: firstFlap?.contour ?? [],
    flaperonPivot: move([fPivot])[0] ?? fPivot,
    flaperonControls: firstFlap?.controls ?? [],
    elements: deployed,
  };
}
